import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { formatBirthdayShort } from '@/lib/birthday';

import {
  GraphMember,
  RELATIONSHIP_OPTIONS,
  findExtendedOption,
  Relationship,
  Side,
  type TreeRelationshipWorkflow,
  useFamilyGraph,
} from '@/contexts/FamilyGraphContext';
import {
  resolveTreeDisplayName,
  resolveTreeFirstName,
} from '@/lib/treeDisplayNameAuthority';

import {
  resolveViewerAnchor,
} from '@/lib/familyPlacement';
import {
} from '@/contexts/FamilyGraphContext';
import {
  computePyramidLayout,
  computePyramidLayoutCanonicalBranchShadow,
  computePyramidLayoutCanonicalLayoutShadow,
  computePyramidLayoutCanonicalBranchAndLayoutShadow,
} from './pyramidLayout';
import {
  resolveCanonicalTreeFamilyUnitRetention,
} from '@/governor/tree/CanonicalTreeFamilyUnitRetentionAuthority';
import {
  allocateCanonicalTreeFamilyUnitPalette,
  allocateCanonicalTreeParentLineageSignature,
  allocateCanonicalTreeVisibleClusterPalette,
  resolveCanonicalTreeTopologyClosedViewProjection,
  resolveCanonicalTreeVisibleClusterPaletteClass,
  type CanonicalTreeFamilyUnitIdentityProjection,
  type CanonicalTreeFamilyUnitPaletteVisual,
  type CanonicalTreeParentLineageToken,
  type CanonicalTreeVisibleClusterPaletteClass,
} from '@/governor/tree/CanonicalTreeViewerPresentationAuthority';

import {
  CANONICAL_TREE_ADD_BUTTON_VISUAL_CLASS,
  CANONICAL_TREE_AVATAR_FALLBACK_VISUAL_CLASS,
  CANONICAL_TREE_AVATAR_VISUAL_CLASS,
  CANONICAL_TREE_CARD_ACCENT_VISUAL_CLASS,
  CANONICAL_TREE_CARD_VISUAL_CLASS,
  CANONICAL_TREE_CENTERED_CARD_VISUAL_CLASS,
  CANONICAL_TREE_PENDING_VISUAL_CLASS,
  CANONICAL_TREE_SPOUSE_HEART_ICON_VISUAL_CLASS,
  CANONICAL_TREE_SPOUSE_HEART_VISUAL_CLASS,
  CANONICAL_TREE_SPOUSE_PARTNER_VISUAL_CLASS,
  CANONICAL_TREE_VIEWER_FRAME_VISUAL_CLASS,
  resolveCanonicalTreeConnectorVisualPresentation,
  resolveCanonicalTreeVisualClusterClass,
  resolveCanonicalTreeVisualRootClass,
} from '@/governor/tree/CanonicalTreeVisualPresentationAuthority';

import './canonicalTreeVisualPalette.css';

import FamilyUnitChip, {
  FAMILY_UNIT_CHIP_FIRST_OFFSET,
  FAMILY_UNIT_CHIP_HEIGHT,
  FAMILY_UNIT_CHIP_STACK_STEP,
  FAMILY_UNIT_CHIP_WIDTH,
} from './FamilyUnitChip';
import {
  verifyTreeGeometry,
} from '@/governor/treeGeometryEvidence';
import {
  enforceTreeLayoutRules,
} from '@/governor/tree/TreeLayoutRuleEnforcementAuthority';
import {
  resolveCanonicalTreeRelationshipCreationPolicy,
} from '@/governor/tree/CanonicalTreeRelationshipCreationPolicy';
import {
  resolveCanonicalTreeWorldCanvas,
  resolveCanonicalTreeInteractionSurfaceProjection,
  type CanonicalTreeWorldCanvasMode,
  type CanonicalTreeWorldCanvasResult,
} from '@/governor/tree/CanonicalTreeWorldCanvasAuthority';
import type {
  CanonicalTreeGeometryRole,
} from '@/governor/tree/CanonicalTreeGeometryRoleAuthority';
import type {
  CanonicalTreeGeometryBranchRoot,
} from '@/governor/tree/CanonicalTreeGeometryBranchRootAuthority';
import type {
  CanonicalTreeRelationshipEdge,
} from '@/governor/tree/CanonicalTreeRelationshipEdge';
import type {
  CanonicalViewerMemberProjection,
} from '@/governor/tree/CanonicalTreeViewerProjection';
import type {
  CanonicalTreeRuntimeAuthorityConvergenceResult,
} from '@/governor/tree/CanonicalTreeActiveRuntimeAuthorityConvergenceContract';
import {
  publishTreeGeometryRuntimeEvidence,
  publishTreeRuntimeConvergenceEvidence,
  publishTreeRuntimeLegacyAncestryCausalLocalizationEvidence,
  publishTreeRuntimeCanonicalBranchCounterfactualShadowEvidence,
  publishTreeRuntimeBranchLayoutInteractionCounterfactualEvidence,
} from '@/governor/tree-runtime/TreeRuntimeGovernorBridge';
import type {
  TreeRuntimeSelectorDiagnostics,
} from '@/governor/tree-runtime/TreeRuntimeSelectorDiagnostics';
import type {
  TreeRuntimeSelectorDiscrepancyClassification,
} from '@/governor/tree-runtime/TreeRuntimeSelectorDiscrepancyClassification';
import type {
  TreeRuntimeSelectorDiscrepancySourceClassification,
} from '@/governor/tree-runtime/TreeRuntimeSelectorDiscrepancySourceClassification';
import type {
  TreeRuntimeBranchLayoutAuthoritySnapshot,
} from '@/governor/tree-runtime/TreeRuntimeBranchLayoutAuthoritySnapshot';

import {
  classifyLegacyAncestryResidueCausality,
} from '@/governor/tree-runtime/TreeRuntimeLegacyAncestryResidueCausalLocalization';

import {
  TREE_RUNTIME_CANONICAL_BRANCH_COUNTERFACTUAL_SCHEMA,
  compareTreeRuntimeCanonicalBranchCounterfactualShadow,
  createTreeRuntimeCanonicalBranchCounterfactualShadowPlan,
  isTreeRuntimeCanonicalBranchCounterfactualExecutionEligible,
} from '@/governor/tree-runtime/TreeRuntimeCanonicalBranchCounterfactualShadowProjection';

import {
  createTreeRuntimeBranchLayoutInteractionCounterfactualMatrixPlan,
  isTreeRuntimeBranchLayoutInteractionCounterfactualExecutionEligible,
  isTreeRuntimeBranchLayoutInteractionCounterfactualMatrixPlanAuthorityCoherent,
  isTreeRuntimeCanonicalBranchCounterfactualShadowPlanAuthorityCoherent,
} from '@/governor/tree-runtime/TreeRuntimeBranchLayoutInteractionCounterfactualMatrix';

import {
  compareTreeRuntimeBranchLayoutInteractionFourCellMatrix,
} from '@/governor/tree-runtime/TreeRuntimeBranchLayoutInteractionCounterfactualComparison';





import {
  ZoomIn,
  ZoomOut,
  Locate,
  Heart,
  Plus,
  ShieldCheck,
  Clock,
  User as UserIcon,
  X,
  Eye,
  MessageSquarePlus,
  UserPlus,
} from 'lucide-react';



const relLabel = (r: string) =>
  RELATIONSHIP_OPTIONS.find((o) => o.value === r)?.label || r;

const generationOf = (rel: Relationship): number => {
  switch (rel) {
    case 'great-grandparent':
      return 3;
    case 'grandparent':
      return 2;
    case 'mother':
    case 'father':
    case 'step-parent':
    case 'adopted-parent':
    case 'aunt-uncle':
      return 1;
    case 'sibling':
    case 'spouse':
    case 'cousin':
    case 'unknown':
      return 0;
    case 'child':
    case 'cousin-child':
      return -1;
    default:
      return 0;
  }
};

const isMotherSide = (m: GraphMember) => m.side === 'mother';
const isFatherSide = (m: GraphMember) => m.side === 'father';

// ISSUE 1 FIX (DOB one-day shift, Dec 2026 production):
//   The old implementation called `new Date(m.birthDate)` on a YYYY-MM-DD
//   string and then `toLocaleDateString()` in the user's local timezone.
//   `new Date("1984-10-28")` parses as UTC midnight, then the locale
//   formatter rebases to local time — for any timezone west of UTC
//   (CDT, EST, PST, etc.) that rebase rolls the calendar day BACK ONE,
//   so "10/28/1984" displayed as "Oct 27, 1984".
//   Fix: delegate to `formatBirthdayShort` from `@/lib/birthday` which
//   parses MM/DD/YYYY manually with NO Date object — no timezone math
//   can shift the day. Honors `birthdayVisibility` ('private' / 'year_only').
const formatBirth = (m: GraphMember): string => formatBirthdayShort(m);


const CARD_W = 168;
const CARD_H = 96;
const CARD_GAP_X = 44; // Sibling spacing within the same row+side. Bumped from
                       // 28 → 44 so adjacent cards in dense rows (e.g. Mom +
                       // multiple aunts on tier 1, or several siblings around
                       // YOU on tier 0) breathe and read as separate people.
const TIER_GAP_Y = 184; // Vertical between generations. Slight bump from 170 →
                        // 184 so connector curves between tiers have more
                        // headroom and labels don't graze each other.
const CENTER_GAP = CARD_W + 96; // Gap around the YOU axis between the
                                // mother-side and father-side lanes. Bumped
                                // from CARD_W+40 → CARD_W+96 so direct
                                // relatives (Mom, Dad, siblings) sit clearly
                                // off-axis from YOU instead of crowding the
                                // center spine. Pure visual spacing.
// Extra horizontal cushion between the viewer's DIRECT-LINE branch (parents,
// siblings, own children, grandchildren) and EXTENDED-FAMILY branches
// (aunts/uncles, cousins, niblings, cousin-children). Without this gap an
// aunt-uncle column was landing immediately next to a parent column, and
// cousin cards were re-injecting on tier 0 close enough to the sibling row
// that branches looked merged. Pure visual layout — relationships,
// parent_ids, kinship labels, and connector routing all stay the same.
const BRANCH_GAP_X = 88;
const LONG_PRESS_MS = 500;


interface PositionedCard {
  member: GraphMember | null;
  memberId: string | null;
  isYou?: boolean;
  x: number;
  y: number;
  tier: number;
  side: 'mother' | 'father' | 'self';
  geometryRoot: CanonicalTreeGeometryBranchRoot;
  clusterKey?: string | null;
  /**
   * True when this card landed in the safety-net overflow row (tier -2)
   * because its placement could not be proved from the parent-child graph.
   * Drives a "Needs connection" visual label on the card so the user knows
   * to open it and link a parent — rather than the card silently displaying
   * a confident kinship label it can't actually justify.
   */
  needsConnection?: boolean;
}


export type TreeViewMode = 'full' | 'mother' | 'father' | 'extended';

interface Props {
  /** Canonical graph used for viewer identity, kinship, tiers, and topology. */
  structuralMembers: GraphMember[];
  /** Viewer-projected members allowed to render on the current surface. */
  members: GraphMember[];
  /** Immutable canonical geometry authority for every Tree-visible member. */
  canonicalGeometryRoles?:
    ReadonlyMap<string, CanonicalTreeGeometryRole>;
  /** Canonical edge evidence used by governed nonancestral connectors. */
  canonicalEdges?:
    readonly CanonicalTreeRelationshipEdge[];

  canonicalViewerProjections:
    ReadonlyMap<
      string,
      CanonicalViewerMemberProjection
    >;

  /**
   * CC-TREE-LIVE-003G3T
   *
   * Certified family-of-origin identity derived upstream from canonical
   * parent-child evidence. Presentation consumers may read but never
   * redefine this authority.
   */
  canonicalFamilyUnitIdentity:
    CanonicalTreeFamilyUnitIdentityProjection;

  /** Single immutable production branch/layout authority. */
  canonicalBranchLayoutAuthority:
    TreeRuntimeBranchLayoutAuthoritySnapshot;
  /**
   * Immutable active-runtime convergence evidence.
   *
   * Observation only: this prop does not change layout,
   * visibility, mutation, or viewport behavior.
   */
  canonicalRuntimeConvergence?:
    CanonicalTreeRuntimeAuthorityConvergenceResult;

  /**
   * Sanitized selector diagnostics containing aggregate counts only.
   *
   * No member identity or family-record content is included.
   */
  canonicalSelectorDiagnostics?:
    TreeRuntimeSelectorDiagnostics;

  /**
   * Sanitized, aggregate discrepancy classifications.
   *
   * Member IDs and raw selector evidence are never published.
   */
  canonicalSelectorDiscrepancyClassification?:
    TreeRuntimeSelectorDiscrepancyClassification;

  /**
   * Sanitized terminal-edge and missing-reference source counts.
   *
   * No member identity, edge identity, or raw compatibility reason is included.
   */
  canonicalSelectorDiscrepancySourceClassification?:
    TreeRuntimeSelectorDiscrepancySourceClassification;

  /**
   * True only when FamilyGraphSection is mounted under a valid
   * authenticated application session.
   *
   * No account identity is published with runtime evidence.
   */
  authenticatedSession?: boolean;
  youName: string;
  youPhotoUrl?: string | null;
  /**
   * Optional id of a non-self member to visually re-center the tree on.
   * When set, the layout shifts so this member sits at the (0,0) origin
   * — useful for "Center tree on this person" exploration mode.
   */
  centeredMemberId?: string | null;
  /**
   * Visual filter for which side/group of relatives to display. Purely a
   * presentation toggle — does NOT modify any underlying data.
   *   - 'full': everyone (default)
   *   - 'mother': only relatives on Mom's side (+ shared + you)
   *   - 'father': only relatives on Dad's side (+ shared + you)
   *   - 'extended': only extended kin (aunts/uncles/cousins/great-grandparents/cousin-children)
   */
  viewMode?: TreeViewMode;
  /**
   * Called when the user wants to open a relative's profile or take a quick
   * action against them. The optional `action` indicates intent:
   *   - 'profile' (default): open full profile drawer
   *   - 'memory': open profile drawer pre-focused on the memory composer
   */
  onSelectMember: (m: GraphMember, action?: 'profile' | 'memory') => void;
  /**
   * Open the Add-Relative modal for a relative attached to a tapped node.
   * `parentId` (4th arg) is the id of the node the user tapped "+" from —
   * the modal pre-selects this id as the new member's parent_id when the
   * relationship is 'child' (rule: "the parent is ALWAYS the node the
   * user tapped from"). For YOU's "+ Child", we pass `viewerMemberId`
   * so the viewer is the auto-picked parent. For global / no-tapped-node
   * paths, leave `parentId` undefined and the modal requires an explicit
   * pick before save.
   * `customLabel` (5th arg) — optional display override (e.g. "Godmother",
   * "Mother-in-law", "Second Cousin"). When set, it's persisted to
   * `relationship_override` on the new row so the kinship engine returns
   * the custom string verbatim. Structural `relationship` / parent_ids /
   * placement remain driven by the closest core type.
   */
  onBeginAddRelativeChooser: () => void;
  onAddRelative: (
    rel: Relationship,
    side: Side,
    prefillName?: string,
    parentId?: string,
    customLabel?: string,
    relationshipWorkflow?: TreeRelationshipWorkflow,
  ) => void;
  /** Called when the user wants to clear an active "centered on" exploration. */
  onClearCenter?: () => void;
}




// Quick action menu shown when a relative card is tapped: View Profile,
// Add Memory, or Add Relative. Keeps adding content tied to a specific
// person and avoids any "dead tap" experience.
const QuickActionPopover: React.FC<{
  member: GraphMember;
  x: number;
  y: number;
  onViewProfile: () => void;
  onAddMemory: () => void;
  onAddRelative: () => void;
  onClose: () => void;
}> = ({ member, x, y, onViewProfile, onAddMemory, onAddRelative, onClose }) => {
  return (
    <div
      className="absolute z-40 -translate-x-1/2 bg-white shadow-2xl border border-slate-200 rounded-2xl px-2 py-2 w-[220px]"
      style={{ left: x, top: y }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="px-2 pb-1.5 flex items-center justify-between">
        <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wide truncate">
          {resolveTreeFirstName(member)}
        </div>
        <button
          onClick={onClose}
          className="text-slate-400 hover:text-slate-600 rounded-full p-0.5"
          aria-label="Close"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="flex flex-col">
        <button
          onClick={() => { onViewProfile(); onClose(); }}
          className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-sm font-semibold text-slate-800 hover:bg-fuchsia-50 text-left"
        >
          <Eye className="w-4 h-4 text-fuchsia-600" />
          View Profile
        </button>
        <button
          onClick={() => { onAddMemory(); onClose(); }}
          className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-sm font-semibold text-slate-800 hover:bg-rose-50 text-left"
        >
          <MessageSquarePlus className="w-4 h-4 text-rose-500" />
          Add Memory
        </button>
        <button
          onClick={() => { onAddRelative(); onClose(); }}
          className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-sm font-semibold text-slate-800 hover:bg-emerald-50 text-left"
        >
          <UserPlus className="w-4 h-4 text-emerald-600" />
          Add Relative
        </button>
      </div>
    </div>
  );
};


// Quick-add chooser anchored to a specific member's "+ button" — or to YOU.
//
// REDESIGN (per "Quick Add" spec):
//  - Default view: 8 one-tap buttons covering 90% of cases:
//      Mom, Dad, Brother, Sister, Child, Grandparent, Aunt/Uncle, Cousin
//    (For non-YOU cards, the buttons are CONTEXT-AWARE: "Parent of X",
//    "Sibling of X", "Spouse of X", "Child of X", plus the same broader
//    set so the user can also add upstream/downstream branches without
//    leaving the popover.)
//  - "More options" toggle: expands to extended relationships
//      Step-Mom / Step-Dad / Adopted Parent / Great-Grandparent /
//      Great-Aunt-Uncle / Grandchild / Great-Grandchild / Niece-Nephew /
//      2nd Cousin / Cousin's Child / Mother-in-law / Father-in-law /
//      Brother-in-law / Sister-in-law / Spouse / Sibling / Mom / Dad
//  - "Custom relationship" inline input — user types any label
//    ("Godmother", "Family friend", "Step-cousin once removed"). The
//    closest CORE relationship type drives placement; the typed label
//    wins display via `relationshipOverride`.
//
// Every option calls `onPick(rel, side, parentId?, customLabel?)` and
// the existing AddGraphMemberModal opens prefilled. Placement remains
// AUTOMATIC via existing parent_ids logic — the kinship engine derives
// the displayed kinship label automatically.
const AddRelativePopover: React.FC<{
  member: GraphMember | null;
  isYou: boolean;
  viewerMemberId?: string | null;
  x: number;
  y: number;
  width?: number;
  maxHeight?: number;
  onPick: (
    rel: Relationship,
    side: Side,
    parentId?: string,
    customLabel?: string,
    relationshipWorkflow?: TreeRelationshipWorkflow,
  ) => void;
  onClose: () => void;
}> = ({
  member,
  isYou,
  viewerMemberId,
  x,
  y,
  width = 280,
  maxHeight = 420,
  onPick,
  onClose,
}) => {
  const [showMore, setShowMore] = React.useState(false);
  const [showCustom, setShowCustom] = React.useState(false);
  const [customLabel, setCustomLabel] = React.useState('');
  const [policyMessage, setPolicyMessage] =
    React.useState<string | null>(null);

  // ---- Quick options (8 default buttons) ----
  // For YOU's "+": Mom / Dad / Brother / Sister / Child / Grandparent /
  // Aunt-Uncle / Cousin — the spec's default-eight.
  // For a relative card: same 8 conceptually, but the structural rel
  // is computed RELATIVE TO THAT CARD via existing parent_ids logic so
  // the kinship engine gets the right walks. "Mom of X" / "Dad of X"
  // → grandparent / great-grandparent / aunt-uncle depending on tier
  // (parentRelFor below). "Brother / Sister of X" → sibling-equivalent
  // by mirroring the tapped card's stored relationship.
  const sideForKin: Side = !member
    ? 'self'
    : member.side === 'self' || member.side === 'both'
    ? 'both'
    : member.side;
  const firstName = !member
    ? 'You'
    : resolveTreeFirstName(member);

  // For non-YOU "Parent of X" we promote across tiers (parent → grandparent
  // → great-grandparent), matching the existing behaviour.
  const parentRelFor = (m: GraphMember): Relationship => {
    if (m.relationship === 'mother' || m.relationship === 'father') return 'grandparent';
    if (m.relationship === 'grandparent') return 'great-grandparent';
    return 'aunt-uncle';
  };

  type QuickOpt = {
    key: string;
    label: string;
    rel: Relationship;
    side: Side;
    parentId?: string;
    custom?: string;
    relationshipWorkflow?: TreeRelationshipWorkflow;
    tone: string;
  };

  const youQuickOpts: QuickOpt[] = [
    { key: 'mom',  label: 'Mom',          rel: 'mother',     side: 'mother', tone: 'text-pink-700 hover:bg-pink-50' },
    { key: 'dad',  label: 'Dad',          rel: 'father',     side: 'father', tone: 'text-sky-700 hover:bg-sky-50' },
    { key: 'bro',  label: 'Brother',      rel: 'sibling',    side: 'both',   tone: 'text-slate-700 hover:bg-slate-100' },
    { key: 'sis',  label: 'Sister',       rel: 'sibling',    side: 'both',   tone: 'text-slate-700 hover:bg-slate-100' },
    { key: 'kid',  label: 'Child',        rel: 'child',      side: 'self',   parentId: viewerMemberId ?? undefined, tone: 'text-emerald-700 hover:bg-emerald-50' },
    { key: 'gp',   label: 'Grandparent',  rel: 'grandparent',side: 'both',   tone: 'text-purple-700 hover:bg-purple-50' },
    { key: 'au',   label: 'Aunt / Uncle', rel: 'aunt-uncle', side: 'both',   tone: 'text-amber-700 hover:bg-amber-50' },
    { key: 'cou',  label: 'Cousin',       rel: 'cousin',     side: 'both',   tone: 'text-teal-700 hover:bg-teal-50' },
    // Spouse / partner — OPTIONAL relationship that never blocks parent
    // logic. Picking it opens the modal with relationship='spouse'; saving
    // links via spouse_id (display-only). parent_ids walks remain primary
    // for every other relationship label.
    { key: 'coparent', label: `Co-Parent of ${firstName}`, rel: 'co-parent', side: sideForKin, parentId: member?.id, custom: 'Co-Parent', tone: 'text-cyan-700 hover:bg-cyan-50' },
      { key: 'spouse', label: 'Spouse / Partner', rel: 'spouse', side: 'self', parentId: viewerMemberId ?? undefined, tone: 'text-rose-700 hover:bg-rose-50' },
  ];

  const relativeQuickOpts: QuickOpt[] = !member ? [] : [
    { key: 'parent',  label: `Parent of ${firstName}`,        rel: parentRelFor(member),        side: sideForKin, tone: 'text-slate-800 hover:bg-slate-50' },
    {
      key: 'sib',
      label: `Sibling of ${firstName}`,
      rel: 'unknown',
      side: sideForKin,
      relationshipWorkflow: {
        kind: 'structural',
        connectorMemberId: member.id,
        secondaryConnectorMemberId: null,
        partnershipStatus: 'not-applicable',
        createsAncestry: false,
        inheritsGeneration: true,
        inheritsBranch: true,
      },
      tone: 'text-slate-800 hover:bg-slate-50',
    },
    { key: 'kid',     label: `Child of ${firstName}`,         rel: 'child',                     side: sideForKin, parentId: member?.id, tone: 'text-emerald-700 hover:bg-emerald-50' },
    { key: 'niece',   label: `Niece / Nephew of ${firstName}`,rel: 'child',                     side: sideForKin, parentId: member?.id, custom: 'Niece / Nephew', tone: 'text-emerald-700 hover:bg-emerald-50' },
    { key: 'cou',     label: `Cousin of ${firstName}`,        rel: 'cousin',                    side: sideForKin, tone: 'text-teal-700 hover:bg-teal-50' },
    // Spouse of this relative — OPTIONAL pairing. linkSpouses runs after
    // the row is saved; failure to link is non-fatal and surfaces a
    // toast so the user can retry from the card.
    { key: 'spouse',  label: `Spouse of ${firstName}`,        rel: 'spouse',                    side: sideForKin, parentId: member?.id, tone: 'text-rose-700 hover:bg-rose-50' },
  ];


  const quickOpts: QuickOpt[] = isYou ? youQuickOpts : relativeQuickOpts;

  // ---- More options (extended relationships) ----
  // These all map to existing core relationship types so placement is
  // automatic via parent_ids. Where a custom DISPLAY label is needed
  // (Great-Aunt, Mother-in-law, 2nd Cousin) we pass a customLabel so
  // `relationship_override` is written and the kinship engine returns
  // that label verbatim.
  // SPOUSE BEHAVIOR (RESTORED — OPTIONAL DISPLAY LAYER):
  //   In-law variants that map to coreRelationship='spouse'
  //   (Brother/Sister/Son/Daughter-in-law) are restored — they save the
  //   row first (always succeeds) and then run linkSpouses as a
  //   non-fatal follow-up. parent_ids relationships always win when
  //   labeling, so an in-law is only labeled "Spouse of X" if no
  //   bloodline path exists.
  const moreOpts: QuickOpt[] = isYou
    ? [
        { key: 'step-mom',  label: 'Step-Mom',           rel: 'step-parent',      side: 'mother', tone: 'text-pink-700 hover:bg-pink-50' },
        { key: 'step-dad',  label: 'Step-Dad',           rel: 'step-parent',      side: 'father', tone: 'text-sky-700 hover:bg-sky-50' },
        { key: 'adopt',     label: 'Adopted Parent',     rel: 'adopted-parent',   side: 'both',   tone: 'text-purple-700 hover:bg-purple-50' },
        { key: 'ggp',       label: 'Great-Grandparent',  rel: 'great-grandparent',side: 'both',   tone: 'text-purple-700 hover:bg-purple-50' },
        { key: 'gau',       label: 'Great-Aunt / Uncle', rel: 'aunt-uncle',       side: 'both',   custom: 'Great-Aunt / Uncle', tone: 'text-amber-700 hover:bg-amber-50' },
        { key: 'gchild',    label: 'Grandchild',         rel: 'child',            side: 'self',   parentId: viewerMemberId ?? undefined, custom: 'Grandchild', tone: 'text-emerald-700 hover:bg-emerald-50' },
        { key: 'ggchild',   label: 'Great-Grandchild',   rel: 'child',            side: 'self',   parentId: viewerMemberId ?? undefined, custom: 'Great-Grandchild', tone: 'text-emerald-700 hover:bg-emerald-50' },
        { key: 'niece',     label: 'Niece / Nephew',     rel: 'cousin-child',     side: 'self',   custom: 'Niece / Nephew', tone: 'text-emerald-700 hover:bg-emerald-50' },

        { key: 'couchild',  label: "Cousin's Child",     rel: 'cousin-child',     side: 'both',   tone: 'text-teal-700 hover:bg-teal-50' },
        { key: 'bil',       label: 'Brother-in-law',     rel: 'spouse',           side: 'self',   custom: 'Brother-in-law', tone: 'text-fuchsia-700 hover:bg-fuchsia-50' },
        { key: 'sil',       label: 'Sister-in-law',      rel: 'spouse',           side: 'self',   custom: 'Sister-in-law',  tone: 'text-fuchsia-700 hover:bg-fuchsia-50' },
        { key: 'sonil',     label: 'Son-in-law',         rel: 'spouse',           side: 'self',   custom: 'Son-in-law',     tone: 'text-fuchsia-700 hover:bg-fuchsia-50' },
        { key: 'dauil',     label: 'Daughter-in-law',    rel: 'spouse',           side: 'self',   custom: 'Daughter-in-law', tone: 'text-fuchsia-700 hover:bg-fuchsia-50' },
      ]
    : [
        { key: 'step',      label: `Step-Parent of ${firstName}`,    rel: 'step-parent',     side: sideForKin, tone: 'text-slate-800 hover:bg-slate-50' },
        { key: 'adopt',     label: `Adopted Parent of ${firstName}`, rel: 'adopted-parent',  side: sideForKin, tone: 'text-slate-800 hover:bg-slate-50' },
        { key: 'gp',        label: `Grandparent of ${firstName}`,    rel: parentRelFor(member!), side: sideForKin, custom: 'Grandparent', tone: 'text-purple-700 hover:bg-purple-50' },
        { key: 'gchild',    label: `Grandchild of ${firstName}`,     rel: 'child',           side: sideForKin, parentId: member?.id, custom: 'Grandchild', tone: 'text-emerald-700 hover:bg-emerald-50' },
        { key: 'family-fr', label: `Family Friend`,                  rel: 'unknown',         side: 'self',     custom: 'Family Friend', tone: 'text-amber-700 hover:bg-amber-50' },
      ];



  const handlePick = (opt: QuickOpt) => {
    const option = opt.custom
      ? findExtendedOption(opt.custom)
      : findExtendedOption(opt.rel);

    const optionKey = option?.value ?? opt.rel;
    const policy =
      resolveCanonicalTreeRelationshipCreationPolicy(
        optionKey,
      );

    if (policy.disposition !== 'CREATE') {
      setPolicyMessage(policy.reason);
      return;
    }

    setPolicyMessage(null);

    onPick(
      opt.rel,
      opt.side,
      opt.parentId,
      opt.custom,
      opt.relationshipWorkflow,
    );
    onClose();
  };

  const handleCustomSubmit = () => {
    const label = customLabel.trim();
    if (!label) return;
    // Custom relationships get the closest-logical placement automatically.
    // For YOU we default to side='self' and rel='unknown' so the kinship
    // engine falls back gracefully — the typed label wins via override.
    // For a relative card we attach via parent_ids if they tapped from
    // a specific person, so the new node still sits on that branch.
    const governedOption = findExtendedOption(label);

    if (governedOption) {
      const policy =
        resolveCanonicalTreeRelationshipCreationPolicy(
          governedOption.value,
        );

      if (policy.disposition !== 'CREATE') {
        setPolicyMessage(policy.reason);
        return;
      }

      setPolicyMessage(null);

      onPick(
        governedOption.coreRelationship,
        governedOption.defaultSide ?? 'self',
        member?.id,
        governedOption.displayOverride ?? label,
      );
    } else {
      onPick('unknown', 'self', undefined, label);
    }
    setCustomLabel('');
    setShowCustom(false);
    onClose();
  };

  return (
    <div
      className="absolute z-40 -translate-x-1/2 bg-white shadow-2xl border border-slate-200 rounded-2xl px-3 py-2.5 overflow-y-auto"
      style={{
        left: x,
        top: y,
        width,
        maxHeight,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between pb-1.5">
        <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wide">
          {isYou ? 'Add a relative' : `Who is this to ${firstName}?`}
        </div>
        <button
          onClick={onClose}
          className="text-slate-400 hover:text-slate-600 rounded-full p-0.5"
          aria-label="Close"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Default 8 quick options — one-tap creation */}
      <div className={`grid ${isYou ? 'grid-cols-2' : 'grid-cols-1'} gap-1`}>
        {quickOpts.map((opt) => (
          <button
            key={opt.key}
            onClick={() => handlePick(opt)}
            className={`flex items-center gap-1.5 text-[12px] font-semibold rounded-lg px-2 py-1.5 text-left transition ${opt.tone}`}
            title={`Add ${opt.label}`}
          >
            <Plus className="w-3 h-3 flex-shrink-0" />
            <span className="truncate">{opt.label}</span>
          </button>
        ))}
      </div>

      {/* More Options toggle */}
      <button
        onClick={() => setShowMore((s) => !s)}
        className="w-full mt-2 text-[11px] font-bold text-fuchsia-700 hover:bg-fuchsia-50 rounded-lg px-2 py-1.5 text-left flex items-center justify-between"
      >
        <span>{showMore ? '− Hide more options' : '+ More options'}</span>
        <span className="text-[9px] text-fuchsia-500">
          step / in-law / great / etc.
        </span>
      </button>

      {showMore && (
        <div className="grid grid-cols-1 gap-1 mt-1 pt-1 border-t border-slate-100">
          {moreOpts.map((opt) => (
            <button
              key={opt.key}
              onClick={() => handlePick(opt)}
              className={`flex items-center gap-1.5 text-[11px] font-semibold rounded-lg px-2 py-1.5 text-left transition ${opt.tone}`}
              title={`Add ${opt.label}`}
            >
              <Plus className="w-3 h-3 flex-shrink-0" />
              <span className="truncate">{opt.label}</span>
            </button>
          ))}
        </div>
      )}

      {/* Custom relationship — user-typed label */}
      <button
        onClick={() => setShowCustom((s) => !s)}
        className="w-full mt-2 text-[11px] font-bold text-purple-700 hover:bg-purple-50 rounded-lg px-2 py-1.5 text-left flex items-center justify-between"
      >
        <span>{showCustom ? '− Cancel custom' : '✎ Custom relationship'}</span>
      </button>
      {showCustom && (
        <div className="mt-1 space-y-1.5">
          <input
            type="text"
            value={customLabel}
            onChange={(e) => setCustomLabel(e.target.value)}
            placeholder="Godmother, Family Friend…"
            className="w-full px-2 py-1.5 text-[12px] border border-purple-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleCustomSubmit();
              }
            }}
            autoFocus
          />
          <button
            onClick={handleCustomSubmit}
            disabled={!customLabel.trim()}
            className="w-full bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white text-[11px] font-bold rounded-lg px-2 py-1.5"
          >
            Add with this label
          </button>
          <p className="text-[10px] text-slate-500 leading-tight">
            Custom labels describe the relationship but never create ancestry.
            Choose a governed relationship when a structural connection is required.
          </p>
        </div>
      )}

      {policyMessage && (
        <p
          role="alert"
          className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-2 py-1.5 text-[10px] text-amber-800"
        >
          {policyMessage}
        </p>
      )}

      <p className="text-[10px] text-slate-500 leading-snug pt-2 mt-2 border-t border-slate-100">
        Their relationship to you is filled in automatically — placement
        follows your existing family connections.
      </p>
    </div>
  );
};




const ProfileCard: React.FC<{
  card: PositionedCard;
  centered: boolean;
  youName: string;
  youPhotoUrl?: string | null;
  /**
   * Path-derived display label (e.g. "Mother", "Aunt / Uncle", "Cousin",
   * "Niece / Nephew") computed from the parent_ids graph relative to the
   * viewer. Falls back to the stored `relationship` enum label when no
   * graph-derived label is available. This is what the user sees on the
   * card — never "Unknown relation" for relatives whose path can be proved.
   */
  displayLabel?: string;
  allocatedClusterClass?:
    CanonicalTreeVisibleClusterPaletteClass | null;

  /**
   * CC-TREE-LIVE-003G3T
   *
   * Certified family-of-origin presentation. Null means the member has no
   * certified 003G3T primary family visual and historical fallback remains.
   */
  familyOriginVisual?:
    CanonicalTreeFamilyUnitPaletteVisual | null;

  /**
   * Presentation-only parent-lineage signature tokens.
   * No parent member ids are exposed to the DOM.
   */
  kinshipSignatureSegments?:
    readonly CanonicalTreeParentLineageToken[];

  onOpen: () => void;
  onLongPress: () => void;
}> = ({
  card,
  centered,
  youName,
  youPhotoUrl,
  displayLabel,
  allocatedClusterClass,
  familyOriginVisual = null,
  kinshipSignatureSegments = [],
  onOpen,
  onLongPress,
}) => {

  const pressTimer = useRef<number | null>(null);
  const movedDuringPress = useRef(false);
  const longPressTriggered = useRef(false);

  /*
   * CC-TREE-LIVE-003G3S
   *
   * Normal live rendering receives a set-level allocation.
   * Undefined retains the historical 003G3R singleton resolver
   * only as a compatibility boundary. Explicit null remains
   * fail-closed and does not silently reuse another family color.
   */
  const hasCertifiedFamilyOriginVisual =
    familyOriginVisual?.failClosed === false &&
    Boolean(
      familyOriginVisual.baseClass,
    );

  const visualClusterClass =
    hasCertifiedFamilyOriginVisual
      ? null
      : allocatedClusterClass === undefined
        ? resolveCanonicalTreeVisualClusterClass(
            card.geometryRoot,
            card.clusterKey ?? null,
          )
        : allocatedClusterClass;

  const visualRootClass =
    [
      resolveCanonicalTreeVisualRootClass(
        card.geometryRoot,
      ),

      hasCertifiedFamilyOriginVisual
        ? 'kc-tree-family-origin'
        : null,

      hasCertifiedFamilyOriginVisual
        ? familyOriginVisual?.baseClass
        : visualClusterClass,

      hasCertifiedFamilyOriginVisual
        ? familyOriginVisual?.variantClass
        : null,
    ]
      .filter(Boolean)
      .join(' ');

  const startPress = () => {
    movedDuringPress.current = false;
    longPressTriggered.current = false;

    pressTimer.current = window.setTimeout(() => {
      pressTimer.current = null;
      longPressTriggered.current = true;
      onLongPress();
    }, LONG_PRESS_MS);
  };
  const cancelPress = () => {
    if (pressTimer.current) {
      window.clearTimeout(pressTimer.current);
      pressTimer.current = null;
    }
  };

  const handleClick = () => {
    if (longPressTriggered.current) {
      longPressTriggered.current = false;
      return;
    }

    if (movedDuringPress.current) return;
    onOpen();
  };

  /*
   * CC-TREE-LIVE-003G3T PR review closure.
   *
   * Every certified ProfileCard, including the viewer card, consumes
   * the same ID-free parent-lineage signature presentation.
   */
  const kinshipSignatureRail =
    kinshipSignatureSegments.length > 0
      ? (
          <div
            className="kc-tree-kinship-signature"
            aria-hidden="true"
          >
            {kinshipSignatureSegments.map(
              (
                token,
                index,
              ) => (
                <span
                  key={`lineage-segment-${index}`}
                  className={`kc-tree-kinship-signature-segment ${token}`}
                />
              ),
            )}
          </div>
        )
      : null;

  if (card.isYou) {
    const viewerMember =
      card.member ?? null;

    const viewerTreeName = viewerMember
      ? resolveTreeDisplayName(
          viewerMember,
          youName || 'You',
        )
      : youName || 'You';

    return (
      <div
        className={`${
          visualRootClass
        } ${
          CANONICAL_TREE_VIEWER_FRAME_VISUAL_CLASS
        } ${
          centered
            ? CANONICAL_TREE_CENTERED_CARD_VISUAL_CLASS
            : ''
        } absolute -translate-x-1/2 -translate-y-1/2 rounded-2xl p-[2px] shadow-xl transition ${
          centered
            ? 'scale-[1.03]'
            : 'hover:scale-[1.02]'
        }`}
        style={{ left: card.x, top: card.y, width: CARD_W, height: CARD_H }}
      >
        <button
          onPointerDown={startPress}
          onPointerUp={cancelPress}
          onPointerLeave={cancelPress}
          onPointerCancel={cancelPress}
          onPointerMove={() => { movedDuringPress.current = false; }}
          onClick={handleClick}
          className="w-full h-full rounded-[14px] bg-white flex items-center gap-3 px-3 text-left"
          aria-label="Open your profile"
        >
          <div className={`w-14 h-14 rounded-full overflow-hidden shrink-0 flex items-center justify-center text-white font-bold ${CANONICAL_TREE_AVATAR_VISUAL_CLASS} ${CANONICAL_TREE_AVATAR_FALLBACK_VISUAL_CLASS}`}>
            {youPhotoUrl ? (
              <img src={youPhotoUrl} alt={viewerTreeName} className="w-full h-full object-cover" />
            ) : (
              <span className="text-lg">
                {viewerTreeName.slice(0, 2).toUpperCase()}
              </span>
            )}
          </div>
          <div className="min-w-0 text-left">
            <div className={`text-[10px] font-bold uppercase tracking-widest ${CANONICAL_TREE_CARD_ACCENT_VISUAL_CLASS}`}>
              You
            </div>
            <div className="font-serif font-bold text-slate-900 truncate text-sm leading-tight">
              {viewerTreeName}
            </div>
            <div className="text-[10px] text-slate-500">Center of your tree</div>
          </div>
        </button>

        {kinshipSignatureRail}
      </div>
    );
  }

  const m = card.member!;
  const treeDisplayName =
    resolveTreeDisplayName(m);
  const verified = m.approvalStatus === 'approved';
  const pending = m.approvalStatus === 'pending';



  return (
    <div
      className={`${visualRootClass} ${
        centered
          ? CANONICAL_TREE_CENTERED_CARD_VISUAL_CLASS
          : ''
      } absolute -translate-x-1/2 -translate-y-1/2`}
      style={{ left: card.x, top: card.y, width: CARD_W, height: CARD_H }}
    >
      <button
        onPointerDown={startPress}
        onPointerUp={cancelPress}
        onPointerLeave={cancelPress}
        onPointerCancel={cancelPress}
        onClick={handleClick}
        title={`Open ${m.name}'s profile`}
        className={`w-full h-full bg-white rounded-2xl border shadow-sm hover:shadow-md transition flex items-center gap-2.5 px-2.5 ${CANONICAL_TREE_CARD_VISUAL_CLASS} ${
          pending
            ? CANONICAL_TREE_PENDING_VISUAL_CLASS
            : ''
        }`}
      >
        <div
          className={`relative w-14 h-14 rounded-full overflow-hidden shrink-0 bg-white ${CANONICAL_TREE_AVATAR_VISUAL_CLASS}`}
        >
          {m.photoUrl ? (
            <img
              src={m.photoUrl}
              alt={treeDisplayName}
              className={`w-full h-full object-cover ${pending ? 'opacity-80 grayscale' : ''}`}
            />
          ) : (
            <div className={`w-full h-full ${CANONICAL_TREE_AVATAR_FALLBACK_VISUAL_CLASS} flex items-center justify-center text-white`}>
              <UserIcon className="w-6 h-6" />
            </div>
          )}
          {verified && (
            <span className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-emerald-500 text-white shadow flex items-center justify-center border border-white">
              <ShieldCheck className="w-2.5 h-2.5" />
            </span>
          )}
          {pending && (
            <span className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-amber-500 text-white shadow flex items-center justify-center border border-white">
              <Clock className="w-2.5 h-2.5" />
            </span>
          )}
        </div>
        <div className="min-w-0 flex-1 text-left">
          <div className="font-semibold text-slate-900 text-[13px] leading-tight truncate">
            {treeDisplayName}
          </div>
          <div className={`text-[10px] font-semibold mt-0.5 ${CANONICAL_TREE_CARD_ACCENT_VISUAL_CLASS} truncate`}>
            {/*
             * Prefer the path-derived display label (computed from
             * parent_ids) so a relative whose stored relationship is
             * 'unknown' / 'cousin-child' / 'sibling' but whose parent_ids
             * resolve to e.g. "Aunt / Uncle" or "Cousin" shows the
             * correct label here. Falls back to the stored enum label
             * only when no graph-derived label is available — which
             * preserves the existing UX for legacy rows that never had
             * parent_ids linked.
             */}
            {displayLabel || relLabel(m.relationship)}
          </div>

          {formatBirth(m) && (
            <div className="text-[10px] text-slate-500 truncate">{formatBirth(m)}</div>
          )}
          {m.livingStatus === 'deceased' && (
            <div className="text-[9px] italic text-slate-400">in memoriam</div>
          )}
        </div>
      </button>

      {kinshipSignatureRail}

      {!card.isYou && (
        <button
          className="absolute bottom-2 left-2 w-7 h-7 rounded-full bg-sky-500 hover:bg-sky-600 text-white shadow-lg flex items-center justify-center border-2 border-white transition"
          aria-label={`Message ${m.name}`}
          title={`Message ${m.name}`}
        >
          💬
        </button>
      )}
    </div>
  );
};

const FamilyTreeView: React.FC<Props> = ({
  structuralMembers,
  members,
  canonicalGeometryRoles,
  canonicalEdges,
  canonicalViewerProjections,
  canonicalFamilyUnitIdentity,
  canonicalBranchLayoutAuthority,
  canonicalRuntimeConvergence,
  canonicalSelectorDiagnostics,
  canonicalSelectorDiscrepancyClassification,
  canonicalSelectorDiscrepancySourceClassification,
  authenticatedSession = false,
  youName,
  youPhotoUrl,
  centeredMemberId,
  viewMode = 'full',
  onSelectMember,
  onBeginAddRelativeChooser,
  onAddRelative,
  onClearCenter,
}) => {
  const viewerMemberId =
    canonicalBranchLayoutAuthority
      .viewerMemberId;

  // phase12a-tree-live-event-safe
  useEffect(() => {
    try {
      import('@/governor/events/GovernorEventBus').then(({ dispatchGovernorEvent }) => {
        dispatchGovernorEvent({
          type: 'member_updated',
          source: 'FamilyTreeView',
          payload: {
            producer: 'FamilyTreeView',
            action: 'tree_view_opened',
            phase: '12A',
            memberCount: Array.isArray(members) ? members.length : 0,
            centeredMemberId,
            viewerMemberId,
            viewMode,
            note: 'Family tree view emitted first live Governor tree event.'
          }
        } as any);
      });
    } catch {}
  }, [members?.length, centeredMemberId, viewerMemberId, viewMode]);



  /*
   * CC-TREE-LIVE-003G2B
   *
   * Publish mounted convergence evidence through the existing
   * Governor event surface. Observation only.
   */
  useEffect(() => {
    if (
      !canonicalRuntimeConvergence ||
      !canonicalSelectorDiagnostics ||
      !canonicalSelectorDiscrepancyClassification ||
      !canonicalSelectorDiscrepancySourceClassification ||
      !authenticatedSession
    ) {
      return;
    }

    /*
     * CC-TREE-LIVE-003G2D
     *
     * Publish authenticated mounted convergence through the
     * Tree Runtime Governor bridge. No account or member
     * identity is included.
     */
    try {
      publishTreeRuntimeConvergenceEvidence({
        authenticatedSession: true,
        convergence:
          canonicalRuntimeConvergence,
        selectorDiagnostics:
          canonicalSelectorDiagnostics,
        selectorDiscrepancyClassification:
          canonicalSelectorDiscrepancyClassification,
        selectorDiscrepancySourceClassification:
          canonicalSelectorDiscrepancySourceClassification,
        source: 'FamilyTreeView',
      });
    } catch {}

    try {
      import(
        '@/governor/events/GovernorEventBus'
      ).then(({ dispatchGovernorEvent }) => {
        dispatchGovernorEvent({
          type: 'member_updated',
          source: 'FamilyTreeView',
          payload: {
            producer: 'FamilyTreeView',
            action:
              'tree_runtime_convergence_observed',
            milestone:
              'CC-TREE-LIVE-003G2B',
            status:
              canonicalRuntimeConvergence.status,
            blockers:
              canonicalRuntimeConvergence.blockers,
            productionVisibleMemberCount:
              canonicalRuntimeConvergence
                .productionVisibleMemberIds
                .length,
            missingProjectionMemberIds:
              canonicalRuntimeConvergence
                .missingProjectionMemberIds,
            missingGeometryRoleMemberIds:
              canonicalRuntimeConvergence
                .missingGeometryRoleMemberIds,
            missingConnectorEdgeIds:
              canonicalRuntimeConvergence
                .missingConnectorEdgeIds,
            permitsLegacyFallback:
              canonicalRuntimeConvergence
                .permitsLegacyFallback,
            permitsPartialCanonicalRendering:
              canonicalRuntimeConvergence
                .permitsPartialCanonicalRendering,
          },
        } as any);
      });
    } catch {}
  }, [
    canonicalRuntimeConvergence,
    canonicalSelectorDiagnostics,
    canonicalSelectorDiscrepancyClassification,
    canonicalSelectorDiscrepancySourceClassification,
    authenticatedSession,
  ]);


  // ─── Bulk-write layout lock ────────────────────────────────────────────
  // While the FamilyGraphContext reports `isBulkWriting === true` (i.e.
  // a save batch is open OR a relationship write is in flight), we hold
  // the current zoom/pan steady. Without this, every optimistic
  // setMembers update inside a rapid mom→dad→grandparent→sibling
  // sequence would change `bounds.minX/maxX/minY/maxY`, retrigger the
  // auto-fit effect, and cause the tree to jump / collapse / inflate
  // between every step. The lock collapses the entire batch into ONE
  // post-save layout recompute (when isBulkWriting flips back to false).
  // Pure presentation — never blocks any write or modifies any data.
  const { isBulkWriting } = useFamilyGraph();

  const wrapRef = useRef<HTMLDivElement>(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });

  const viewportSnapshotRef = useRef({
    zoom: 1,
    pan: {
      x: 0,
      y: 0,
    },
  });

  useEffect(() => {
    viewportSnapshotRef.current = {
      zoom,
      pan,
    };
  }, [
    zoom,
    pan,
  ]);

  const [dragging, setDragging] = useState(false);
  const dragRef = useRef<{ x: number; y: number; px: number; py: number } | null>(null);
  // Multi-pointer pinch-zoom state. Tracks every active pointer (finger /
  // stylus / mouse) inside the canvas by pointerId so we can detect when
  // TWO fingers are down at once and run a pinch gesture.
  //
  // Rule: while exactly 2 pointers are active, the distance between them
  // controls zoom and the midpoint between them controls pan. While 1
  // pointer is active we do plain drag-to-pan. While 0 are active we are
  // idle. When a 2-finger gesture ends and a single finger remains, we
  // seamlessly resume drag-to-pan from the remaining finger's position
  // (no jump). Without this multi-pointer handling the existing single
  // pointerdown→move→up flow could only pan, never pinch — exactly the
  // bug reported ("can't pinch-expand or smoothly zoom inside the tree
  // canvas itself").
  const pointersRef = useRef<Map<number, { x: number; y: number }>>(new Map());
  const pinchRef = useRef<{
    startDist: number;
    startZoom: number;
    startMid: { x: number; y: number };
    startPan: { x: number; y: number };
  } | null>(null);
  // Initial 0,0 so the auto-fit effect waits for the FIRST real measurement
  // before computing zoom — prevents a premature "fit" with a stale 1000x700
  // default that would later look compressed once the real container size
  // arrives via ResizeObserver.
  const [containerSize, setContainerSize] = useState({ w: 0, h: 0 });

  // Which card has its "+ button" popover open
  const [addPopoverFor, setAddPopoverFor] = useState<string | 'you' | null>(null);
  // Which member card has its "View Profile / Add Memory / Add Relative"
  // quick action menu open (single-tap menu).
  const [quickPopoverFor, setQuickPopoverFor] = useState<string | null>(null);
  // Governed viewport framing request:
  //   readable — center the current viewer or explicitly selected relative
  //              at the governed readable scale;
  //   overview — frame the complete current graph at the governed overview
  //              minimum and report whether the complete graph actually fits;
  //   null     — preserve manual pan or zoom without automatic reframing.
  const [requestedFrameMode, setRequestedFrameMode] =
    useState<CanonicalTreeWorldCanvasMode | null>('readable');

  const [worldCanvasEvidence, setWorldCanvasEvidence] =
    useState<CanonicalTreeWorldCanvasResult | null>(null);


  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const update = () => {
      const r = el.getBoundingClientRect();
      setContainerSize({ w: r.width, h: r.height });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Lay out cards into strict horizontal rows (one per generation), with
  // mother-side stacking left of "You" and father-side stacking right.
  // ─── STRICT GENERATIONAL PYRAMID LAYOUT ───────────────────────────────
  //
  // The previous tier-based branch layout has been REPLACED with a clean,
  // deterministic pyramid algorithm:
  //
  //   • Every person is assigned a generation index (kinship engine).
  //   • Couples (members sharing a child OR linked via spouse_id) are
  //     rendered as a LOCKED pair side-by-side; their children's
  //     connector originates at the couple midpoint.
  //   • YOU is always horizontally centered at x=0 on tier 0.
  //   • Mother-side branches LEFT of center; father-side RIGHT.
  //   • Each parent's children form a horizontal cluster directly
  //     beneath them — clusters expand outward, never flatten.
  //   • Subtree-width memoization guarantees no overlaps.
  //   • Pure presentation: parent_ids / names / sides / stored
  //     relationships / invites / billing / uploads are NEVER touched.
  //
  // See `pyramidLayout.ts` for the full algorithm.
  const layout = useMemo(
    () =>
      computePyramidLayout({
          structuralMembers,
          visibleMembers: members,
          canonicalGeometryRoles,
          canonicalEdges,
          branchLayoutAuthority:
            canonicalBranchLayoutAuthority,
        centeredMemberId,
        youName,
      }),
    [
      structuralMembers,
      members,
      canonicalGeometryRoles,
      canonicalEdges,
      canonicalBranchLayoutAuthority,
      centeredMemberId,
      youName,
    ],
  );

  // Map the new layout's edge / spouse / card / bounds shapes onto the
  // names the rest of this file already uses, so the rendering layer
  // (popovers, drag/zoom, displayLabelById, connectors, viewport framing) keeps
  // working without further changes.
  const canonicalBranchCounterfactualShadowPlan =
    useMemo(
      () =>
        createTreeRuntimeCanonicalBranchCounterfactualShadowPlan(
          canonicalBranchLayoutAuthority.branch,
        ),
      [canonicalBranchLayoutAuthority],
    );

  /*
   * CC-TREE-LIVE-003G3E4
   *
   * Cheap four-cell experiment plan only.
   * Creating this plan does not execute either shadow layout.
   */
  const branchLayoutInteractionMatrixPlan =
    useMemo(
      () =>
        createTreeRuntimeBranchLayoutInteractionCounterfactualMatrixPlan({
          authority:
            canonicalBranchLayoutAuthority,
          semanticPlan:
            canonicalBranchCounterfactualShadowPlan,
        }),
      [
        canonicalBranchLayoutAuthority,
        canonicalBranchCounterfactualShadowPlan,
      ],
    );

  const cards: PositionedCard[] = useMemo(() => {
    return layout.cards.map((c) => ({
      member: c.member,
      memberId: c.memberId,
      isYou: c.isYou,
      x: c.x,
      y: c.y,
      tier: c.tier,
      side: c.side,
      geometryRoot: c.geometryRoot,
      clusterKey: c.clusterKey ?? null,
      needsConnection: c.needsConnection,
    }));
  }, [layout]);

  // Convert pyramid edges into the cards-relative shape the connectors
  // useMemo expects: a synthetic "from" / "to" PositionedCard pair holding
  // just the (x, y) of each endpoint.
  const edges = useMemo(
    () =>
      layout.edges.map((e) => ({
        fromMemberIds: [...e.fromMemberIds],
        toMemberId: e.toMemberId,
        from: { x: e.from.x, y: e.from.y } as PositionedCard,
        to: { x: e.to.x, y: e.to.y } as PositionedCard,
        side: e.side,
        verified: e.verified,
        key: e.key,
        routeRoot: e.routeRoot,
        routeStatus: e.routeStatus,

        /*
         * CC-TREE-LIVE-003G3E2
         *
         * Private in-memory causal evidence only.
         * Never published with member or edge identity.
         */
        viewerTerminalEligibility:
          e.viewerTerminalEligibility,

        routePoints:
          e.routePoints.map(
            point => ({
              x:
                point.x,
              y:
                point.y,
            }),
          ),
        rejectedObstacleMemberIds:
          [...e.rejectedObstacleMemberIds],
      })),
    [layout],
  );

  const spouseLinks = useMemo(
    () =>
      layout.spouseLinks.map((l) => ({
        aMemberId: l.aMemberId,
        bMemberId: l.bMemberId,
        a: { x: l.a.x, y: l.a.y } as PositionedCard,
        b: { x: l.b.x, y: l.b.y } as PositionedCard,
      })),
    [layout],
  );

  type FamilyUnitChipEntry = {
    member: GraphMember;
    derivedLabel: string;
  };
  const familyUnitChips = useMemo(() => {
    /**
     * @returns Map<anchorMemberId, FamilyUnitChipEntry[]>
     *   — every chip to render under each placed-and-non-overflow card.
     * Side effect (intentional): chip-target ids are also returned via
     * `dropFromCards` so the cards.map can skip them.
     */
    const chips = new Map<string, FamilyUnitChipEntry[]>();
    const dropFromCards = new Set<string>();
    if (!cards.length) return { chips, dropFromCards };
    // Index placed cards by member id, classifying them as "anchor"
    // (any non-overflow placed card) vs "overflow" (needsConnection
    // band). Anchors are valid spouse-target placements; overflow
    // members are NOT — putting a chip on someone in Needs Connection
    // doesn't help anyone.
    const anchorCardByMemberId = new Map<string, PositionedCard>();
    const overflowMemberIds = new Set<string>();
    const youCardMemberId = (() => {
      // The YOU card retains the canonical viewer member when claimed.
      // Spouse-of-viewer is already drawn by pyramidLayout as
      // a locked couple, so we never produce a chip for the viewer's
      // OWN partner here. We still skip building chips when the anchor
      // IS the YOU card so the viewer's own partner is never duplicated
      // as both a locked couple card and a Family Unit chip.
      const youCard = cards.find((c) => c.isYou);
      return youCard ? '__you__' : null;
    })();
    for (const c of cards) {
      if (c.isYou) continue;
      if (!c.member) continue;
      if (c.needsConnection || c.tier <= -2) {
        overflowMemberIds.add(c.member.id);
      } else {
        anchorCardByMemberId.set(c.member.id, c);
      }
    }
    if (anchorCardByMemberId.size === 0) {
      return { chips, dropFromCards };
    }
    // For every member in the loaded graph, see if they have a
    // spouse_id link (forward OR reverse) to a placed anchor card.
    // The "already-coupled" check below ensures we don't double-render
    // when pyramidLayout already drew them side-by-side with a heart.
    //
    // COUPLE CONSISTENCY FIX (universal heart connector):
    //   pyramidLayout's buildCouples now forms a couple unit (heart
    //   connector + side-by-side render) when EITHER partner stores a
    //   spouse_id pointing at the other — reciprocity is preferred but
    //   no longer required. To keep the chip dedup in lockstep we mark
    //   the pair as "already-coupled" using the same one-sided rule:
    //   if BOTH partners are placed as non-overflow anchor cards AND at
    //   least ONE direction of spouse_id agrees, pyramid drew them as a
    //   couple and the chip MUST be suppressed (otherwise we'd double-
    //   render the heart + a redundant "Spouse" chip below one card).
    const couplePairKeys = new Set<string>();
    for (const c of cards) {
      if (c.isYou || !c.member) continue;
      if (c.needsConnection) continue;
      const sId = c.member.spouseId;
      if (!sId) continue;
      const partner = anchorCardByMemberId.get(sId);
      if (!partner || !partner.member) continue;
      // Either forward (c → partner) OR reverse (partner → c) qualifies.
      // partner.member.spouseId === c.member.id  → reciprocal
      // partner.member.spouseId === null         → one-sided (forward)
      // Anything else (partner points at a third party) → NOT a couple.
      const partnerSpouse = partner.member.spouseId ?? null;
      if (partnerSpouse !== null && partnerSpouse !== c.member.id) continue;
      const k = c.member.id < sId ? `${c.member.id}::${sId}` : `${sId}::${c.member.id}`;
      couplePairKeys.add(k);
    }

    const viewerAnchor = resolveViewerAnchor(structuralMembers, viewerMemberId);
    const viewerId = viewerAnchor?.id ?? null;
    const seenChipKeys = new Set<string>();
    const considerChip = (
      anchor: GraphMember,
      partner: GraphMember,
    ): void => {
      if (!partner || partner.approvalStatus === 'rejected') return;
      if (partner.id === anchor.id) return;

      const retentionDecision =
        resolveCanonicalTreeFamilyUnitRetention({
          members:
            structuralMembers,
          memberId:
            partner.id,
        });

      if (
        retentionDecision.retainTreeCard ||
        !retentionDecision.mayRenderFamilyUnitChip
      ) {
        return;
      }

      // Don't chip the YOU row — pyramidLayout draws viewer's own
      // spouse as part of the YOU couple unit.
      if (viewerId && partner.id === viewerId) return;
      // Skip if partner IS placed as a real anchor card AND the pair
      // is reciprocally coupled — heart connector handles it.
      const pairKey = anchor.id < partner.id
        ? `${anchor.id}::${partner.id}`
        : `${partner.id}::${anchor.id}`;
      if (couplePairKeys.has(pairKey)) return;
      if (seenChipKeys.has(pairKey)) return;
      seenChipKeys.add(pairKey);
      // G2 presentation convergence:
      // consume the already-derived canonical viewer semantic descriptor.
      // Missing canonical evidence fails safely and never invokes a
      // second kinship engine.
      const derivedLabel =
        canonicalViewerProjections
          .get(partner.id)
          ?.kinshipSemantics.primary.treeLabel
          ?.trim() ||
        'Needs connection';
      const list = chips.get(anchor.id) ?? [];
      list.push({ member: partner, derivedLabel });
      chips.set(anchor.id, list);
      // If the partner currently lives in the overflow row, drop them
      // from the main `cards` list — the chip is now their visible
      // home on the tree.
      if (
        overflowMemberIds.has(partner.id) &&
        retentionDecision
          .mayReplaceTreeCardWithChip
      ) {
        dropFromCards.add(partner.id);
      }
    };
    // Forward + reverse spouse_id sweep.
    for (const m of members) {
      if (m.approvalStatus === 'rejected') continue;
      // Forward: m.spouseId points at an anchor → m might be a chip.
      if (m.spouseId) {
        const anchor = anchorCardByMemberId.get(m.spouseId);
        if (anchor && anchor.member) {
          // m is the partner; anchor.member is the placed bloodline row.
          // We render the chip on anchor.member's card UNLESS m is also
          // already an anchor card (handled by couplePairKeys above).
          if (!anchorCardByMemberId.has(m.id)) {
            considerChip(anchor.member, m);
          } else if (!couplePairKeys.has(
            anchor.member.id < m.id
              ? `${anchor.member.id}::${m.id}`
              : `${m.id}::${anchor.member.id}`,
          )) {
            // Both placed but NOT recognised as a couple by pyramid
            // (one-sided spouse_id) — still surface the FamilyUnit
            // chip so the user sees the partnership.
            considerChip(anchor.member, m);
          }
        }
      }
    }
    // Reverse: someone else's spouseId points at an anchor.
    // (Already covered by the forward pass since we iterate every
    // member with spouseId set; the anchor is whichever side has a
    // placed card.) No extra pass needed.
    return { chips, dropFromCards };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cards, members, structuralMembers, viewerMemberId]);

  // Apply the FamilyUnit drop-list — any overflow card whose member
  // now appears as a chip on someone else's anchor is removed from
  // the main render pipeline. The chip becomes their visible home.
  const visibleCards = useMemo(() => {
    if (familyUnitChips.dropFromCards.size === 0) return cards;
    return cards.filter((c) => !c.member || !familyUnitChips.dropFromCards.has(c.member.id));
  }, [cards, familyUnitChips.dropFromCards]);

  const canonicalFinalProjection = useMemo(() => {
    const finalCards = visibleCards;

    const visibleMemberIds = new Set(
      finalCards
        .map((card) => card.memberId)
        .filter((id): id is string => Boolean(id)),
    );

    const finalCardByMemberId = new Map(
      finalCards
        .filter(
          (card): card is PositionedCard & {
            memberId: string;
          } => Boolean(card.memberId),
        )
        .map((card) => [card.memberId, card]),
    );

    const companionChips = Array.from(
      familyUnitChips.chips.entries(),
    ).flatMap(([anchorMemberId, chips]) => {
      const anchor = finalCardByMemberId.get(
        anchorMemberId,
      );

      if (!anchor) return [];

      return chips.map((chip, stackIndex) => ({
        memberId: chip.member.id,
        anchorMemberId,
        stackIndex,
        x: anchor.x,
        top:
          anchor.y +
          CARD_H / 2 +
          FAMILY_UNIT_CHIP_FIRST_OFFSET +
          stackIndex * FAMILY_UNIT_CHIP_STACK_STEP,
        width: FAMILY_UNIT_CHIP_WIDTH,
        height: FAMILY_UNIT_CHIP_HEIGHT,
      }));
    });

    const finalEdges = edges.filter((edge) => {
      if (!visibleMemberIds.has(edge.toMemberId)) {
        return false;
      }

      return edge.fromMemberIds.every((id) =>
        visibleMemberIds.has(id),
      );
    });

    const finalSpouseLinks = spouseLinks.filter(
      (link) =>
        visibleMemberIds.has(link.aMemberId) &&
        visibleMemberIds.has(link.bMemberId),
    );

    if (finalCards.length === 0) {
      return {
        cards: finalCards,
        edges: finalEdges,
        spouseLinks: finalSpouseLinks,
        companionChips,
        bounds: layout.bounds,
      };
    }

    let minX = Number.POSITIVE_INFINITY;
    let maxX = Number.NEGATIVE_INFINITY;
    let minY = Number.POSITIVE_INFINITY;
    let maxY = Number.NEGATIVE_INFINITY;

    for (const card of finalCards) {
      minX = Math.min(minX, card.x - CARD_W / 2);
      maxX = Math.max(maxX, card.x + CARD_W / 2);
      minY = Math.min(minY, card.y - CARD_H / 2);
      maxY = Math.max(maxY, card.y + CARD_H / 2);
    }

    for (const chip of companionChips) {
      minX = Math.min(
        minX,
        chip.x - chip.width / 2,
      );
      maxX = Math.max(
        maxX,
        chip.x + chip.width / 2,
      );
      minY = Math.min(minY, chip.top);
      maxY = Math.max(
        maxY,
        chip.top + chip.height,
      );
    }

    for (const edge of finalEdges) {
      for (const point of edge.routePoints) {
        minX = Math.min(minX, point.x);
        maxX = Math.max(maxX, point.x);
        minY = Math.min(minY, point.y);
        maxY = Math.max(maxY, point.y);
      }
    }

    for (const link of finalSpouseLinks) {
      for (const point of [link.a, link.b]) {
        minX = Math.min(minX, point.x);
        maxX = Math.max(maxX, point.x);
        minY = Math.min(minY, point.y);
        maxY = Math.max(maxY, point.y);
      }
    }

    return {
      cards: finalCards,
      edges: finalEdges,
      spouseLinks: finalSpouseLinks,
      companionChips,
      bounds: {
        minX,
        maxX,
        minY,
        maxY,
        w: maxX - minX,
        h: maxY - minY,
      },
    };
  }, [
    visibleCards,
    edges,
    spouseLinks,
    familyUnitChips.chips,
    layout.bounds,
  ]);

  const finalProjection = useMemo(() => {
    /*
     * CC-TREE-LIVE-003G2X
     *
     * Presentation projection consumes the already-complete canonical
     * geometry/evidence world. It cannot move cards, reroute connectors,
     * rebuild spouse links, or change the canonical full-world bounds.
     */
    const candidateCardMemberIds =
      canonicalFinalProjection
        .cards.flatMap(
          card =>
            card.memberId
              ? [card.memberId]
              : [],
        );

    const viewProjection =
      resolveCanonicalTreeTopologyClosedViewProjection({
        viewMode,

        viewerMemberId,

        cardMemberIds:
          candidateCardMemberIds,

        roleByMemberId:
          canonicalGeometryRoles ??
          new Map<
            string,
            CanonicalTreeGeometryRole
          >(),

        edges:
          canonicalFinalProjection
            .edges,

        spouseLinks:
          canonicalFinalProjection
            .spouseLinks,
      });

    const projectionFailedClosed =
      viewProjection
        .unresolvedSelectedMemberIds
        .length > 0;

    const projectedVisibleMemberIds =
      projectionFailedClosed
        ? new Set<string>(
            viewerMemberId
              ? [viewerMemberId]
              : [],
          )
        : viewProjection
            .visibleMemberIds;

    const finalCards =
      canonicalFinalProjection
        .cards.filter(
          card =>
            card.memberId
              ? projectedVisibleMemberIds
                  .has(card.memberId)
              : viewMode === 'full',
        );

    const visibleMemberIds =
      new Set(
        finalCards.flatMap(
          card =>
            card.memberId
              ? [card.memberId]
              : [],
        ),
      );

    /*
     * A view may only retain complete connectors from the canonical
     * evidence world. No partial hyperedge, reroute, or invented line
     * is permitted.
     */
    const finalEdges =
      canonicalFinalProjection
        .edges.filter(
          edge =>
            visibleMemberIds.has(
              edge.toMemberId,
            ) &&
            edge.fromMemberIds.every(
              memberId =>
                visibleMemberIds.has(
                  memberId,
                ),
            ),
        );

    const finalSpouseLinks =
      canonicalFinalProjection
        .spouseLinks.filter(
          link =>
            visibleMemberIds.has(
              link.aMemberId,
            ) &&
            visibleMemberIds.has(
              link.bMemberId,
            ),
        );

    /*
     * Companion chips are nonancestral partner presentation and remain
     * a Full Tree surface only. Their canonical evidence still exists
     * independently in canonicalFinalProjection.
     */
    const companionChips =
      viewMode === 'full'
        ? canonicalFinalProjection
            .companionChips
        : [];

    return {
      cards:
        finalCards,

      edges:
        finalEdges,

      spouseLinks:
        finalSpouseLinks,

      companionChips,

      /*
       * Every selector consumes the exact same canonical full-world
       * envelope. View membership cannot collapse or recenter the canvas.
       */
      bounds:
        canonicalFinalProjection
          .bounds,

      viewProjection,
      projectionFailedClosed,
    };
  }, [
    canonicalFinalProjection,
    viewMode,
    viewerMemberId,
    canonicalGeometryRoles,
  ]);

  const finalCards = finalProjection.cards;
  const finalEdges = finalProjection.edges;
  const finalSpouseLinks = finalProjection.spouseLinks;
  const finalCompanionChips =
    finalProjection.companionChips;
  const bounds = finalProjection.bounds;

  /*
   * Geometry verification and publication consume ONLY the immutable
   * canonical full-world projection. Presentation selectors are not
   * observability inputs.
   */
  const canonicalEvidenceCards =
    canonicalFinalProjection.cards;

  const canonicalEvidenceEdges =
    canonicalFinalProjection.edges;

  const canonicalEvidenceSpouseLinks =
    canonicalFinalProjection.spouseLinks;

  const canonicalEvidenceCompanionChips =
    canonicalFinalProjection.companionChips;

  const canonicalEvidenceBounds =
    canonicalFinalProjection.bounds;

  /*
   * CC-TREE-LIVE-003G3T
   *
   * Parent-lineage signature is topology-derived presentation only.
   */
  const canonicalParentLineageSignature =
    useMemo(
      () =>
        allocateCanonicalTreeParentLineageSignature({
          identityProjection:
            canonicalFamilyUnitIdentity,
        }),
      [
        canonicalFamilyUnitIdentity,
      ],
    );

  /*
   * CC-TREE-LIVE-003G3T
   *
   * Primary family colors consume the immutable canonical FULL-WORLD cards
   * before Mom/Dad/Extended presentation filtering.
   *
   * Geometry answers only whether already-certified family units are
   * visually adjacent. It never derives family identity.
   */
  const familyUnitPaletteAllocation =
    useMemo(
      () =>
        allocateCanonicalTreeFamilyUnitPalette({
          candidates:
            canonicalFinalProjection.cards
              .flatMap(
                card => {
                  if (!card.memberId) {
                    return [];
                  }

                  const identity =
                    canonicalFamilyUnitIdentity
                      .identityByMemberId
                      .get(
                        card.memberId,
                      );

                  if (
                    identity?.status !==
                      'CERTIFIED' ||
                    !identity.familyUnitId
                  ) {
                    return [];
                  }

                  return [{
                    familyUnitId:
                      identity.familyUnitId,
                    tier:
                      card.tier,
                    x:
                      card.x,
                    width:
                      CARD_W,
                  }];
                },
              ),
        }),
      [
        canonicalFinalProjection.cards,
        canonicalFamilyUnitIdentity,
      ],
    );

  const familyOriginVisualByMemberId =
    useMemo(
      () => {
        const visualByMemberId =
          new Map<
            string,
            CanonicalTreeFamilyUnitPaletteVisual
          >();

        for (
          const [
            memberId,
            identity,
          ] of
          canonicalFamilyUnitIdentity
            .identityByMemberId
            .entries()
        ) {
          if (
            identity.status !==
              'CERTIFIED' ||
            !identity.familyUnitId
          ) {
            continue;
          }

          const visual =
            familyUnitPaletteAllocation
              .allocationByFamilyUnitId
              .get(
                identity.familyUnitId,
              );

          if (
            visual &&
            visual.failClosed === false &&
            visual.baseClass
          ) {
            visualByMemberId.set(
              memberId,
              visual,
            );
          }
        }

        return visualByMemberId;
      },
      [
        canonicalFamilyUnitIdentity,
        familyUnitPaletteAllocation,
      ],
    );

    /*
   * CC-TREE-LIVE-003G3S
   *
   * Allocate once across the exact card set that reaches rendering.
   * No individual ProfileCard may independently allocate the visible set.
   */
  const visibleClusterPaletteAllocation =
    useMemo(
      () =>
        allocateCanonicalTreeVisibleClusterPalette(
          finalCards.map(
            card => ({
              geometryRoot:
                card.geometryRoot,
              clusterKey:
                card.clusterKey ?? null,
            }),
          ),
        ),
      [finalCards],
    );

const geometryEvidence = useMemo(() => {
    const structuralById = new Map(
      structuralMembers.map(
        member =>
          [
            member.id,
            member,
          ] as const,
      ),
    );

    const baseEvidence = verifyTreeGeometry(
      {
        cards: canonicalEvidenceCards.map((card) => ({
          memberId: card.memberId,
          x: card.x,
          y: card.y,
          tier: card.tier,

          /*
           * CC-TREE-LIVE-003G2Z
           *
           * Production geometry evidence carries structural
           * parent authority into the independent verifier.
           *
           * Missing structural authority becomes an empty
           * parent set rather than inferred parenthood.
           */
          parentIds:
            card.memberId
              ? [
                  ...(
                    structuralById.get(
                      card.memberId,
                    )?.parentIds ??
                    []
                  ),
                ]
              : [],

          isYou: card.isYou,
          needsConnection: card.needsConnection,
        })),
        edges: canonicalEvidenceEdges,
        spouseLinks: canonicalEvidenceSpouseLinks,
        companionChips: canonicalEvidenceCompanionChips,
        bounds: canonicalEvidenceBounds,
        requestedViewerMemberId: viewerMemberId,
        centeredMemberId,
      },
      {
        cardWidth: CARD_W,
        cardHeight: CARD_H,
        rowGap: TIER_GAP_Y,
        tolerance: 0.001,
        bezierSamples: 64,
      },
    );
    const branchPaths =
      canonicalBranchLayoutAuthority
        .branch
        .branchPathByMemberId;
    const ruleReport = enforceTreeLayoutRules(
      canonicalEvidenceCards.flatMap((card) => {
        if (!card.memberId) return [];
        const structural = structuralById.get(card.memberId);
        if (!structural) return [];

        return [{
          memberId: card.memberId,
          x: card.x,
          y: card.y,
          tier: card.tier,
          parentIds: [...(structural.parentIds ?? [])],
          branchPath: branchPaths.get(card.memberId) ?? null,
          spouseId: structural.spouseId ?? null,
          isYou: card.isYou,
        }];
      }),
      {
        rowGap: TIER_GAP_Y,
        cardWidth: CARD_W,
        tolerance: 0.001,
      },
    );

    if (ruleReport.status === 'PASS') return baseEvidence;

    const ruleIssues = ruleReport.violations.map((violation) => ({
      status: 'FAIL' as const,
      rule: violation.rule,
      details: violation.details,
      memberIds: violation.memberIds,
    }));

    const nonPassIssues = baseEvidence.issues.filter(
      (issue) => issue.rule !== 'TREE-GEOMETRY-EVIDENCE',
    );

    return {
      ...baseEvidence,
      status: 'FAIL' as const,
      issues: [...nonPassIssues, ...ruleIssues],
      issueCount: nonPassIssues.length + ruleIssues.length,
    };
  }, [
    canonicalEvidenceCards,
    canonicalEvidenceEdges,
    canonicalEvidenceSpouseLinks,
    canonicalEvidenceCompanionChips,
    canonicalEvidenceBounds,
    viewerMemberId,
    centeredMemberId,
    structuralMembers,
    canonicalBranchLayoutAuthority,
  ]);

  /*
   * CC-TREE-LIVE-003G3E2
   *
   * Private causal localization occurs before sanitization.
   *
   * Member IDs and edge keys are used only for the in-memory join.
   * The returned object contains aggregate counts only.
   */
  const legacyAncestryCausalLocalization =
    useMemo(
      () =>
        classifyLegacyAncestryResidueCausality(
          canonicalEvidenceEdges.map(
            edge => ({
              key: edge.key,
              fromMemberIds:
                [...edge.fromMemberIds],
              toMemberId:
                edge.toMemberId,
              routeRoot:
                edge.routeRoot,
              routeStatus:
                edge.routeStatus,
              viewerTerminalEligibility:
                edge.viewerTerminalEligibility,
            }),
          ),
          geometryEvidence.issues,
          canonicalBranchLayoutAuthority,
        ),
      [
        canonicalEvidenceEdges,
        geometryEvidence,
        canonicalBranchLayoutAuthority,
      ],
    );

  /*
   * CC-TREE-LIVE-003G3E3 REVIEW CLOSURE
   *
   * Counterfactual execution is diagnostic work, not baseline
   * Tree-render work. The expensive second layout and paired
   * geometry verification are therefore eligible only for the
   * exact authenticated legacy-fallback failing cohort that
   * contains branch divergence and usable canonical evidence.
   */
  const canonicalBranchCounterfactualEligible =
    useMemo(
      () =>
        isTreeRuntimeCanonicalBranchCounterfactualExecutionEligible({
          authenticatedSession,
          plan:
            canonicalBranchCounterfactualShadowPlan,
          causalLocalization:
            legacyAncestryCausalLocalization,
        }),
      [
        authenticatedSession,
        canonicalBranchCounterfactualShadowPlan,
        legacyAncestryCausalLocalization,
      ],
    );

  const canonicalBranchCounterfactualShadowLayout =
    useMemo(
      () => {
        if (
          !canonicalBranchCounterfactualEligible
        ) {
          return null;
        }

        try {
          return computePyramidLayoutCanonicalBranchShadow(
            {
              structuralMembers,
              visibleMembers:
                members,
              canonicalGeometryRoles,
              canonicalEdges,
              branchLayoutAuthority:
                canonicalBranchLayoutAuthority,
              centeredMemberId,
              youName,
            },
            canonicalBranchCounterfactualShadowPlan,
          );
        } catch {
          return null;
        }
      },
      [
        canonicalBranchCounterfactualEligible,
        structuralMembers,
        members,
        canonicalGeometryRoles,
        canonicalEdges,
        canonicalBranchLayoutAuthority,
        centeredMemberId,
        youName,
        canonicalBranchCounterfactualShadowPlan,
      ],
    );

  const canonicalBranchCounterfactualComparison =
    useMemo(
      () =>
        canonicalBranchCounterfactualShadowLayout
          ? compareTreeRuntimeCanonicalBranchCounterfactualShadow({
              controlLayout:
                layout,
              shadowLayout:
                canonicalBranchCounterfactualShadowLayout,
              structuralMembers,
              controlBranchPathByMemberId:
                canonicalBranchLayoutAuthority
                  .branch
                  .branchPathByMemberId,
              shadowPlan:
                canonicalBranchCounterfactualShadowPlan,
              productionGeometryEvidence:
                geometryEvidence,
              viewerMemberId,
              centeredMemberId,
              options: {
                cardWidth:
                  CARD_W,
                cardHeight:
                  CARD_H,
                rowGap:
                  TIER_GAP_Y,
                tolerance:
                  0.001,
                bezierSamples:
                  64,
              },
            })
          : null,
      [
        layout,
        canonicalBranchCounterfactualShadowLayout,
        structuralMembers,
        canonicalBranchLayoutAuthority,
        canonicalBranchCounterfactualShadowPlan,
        geometryEvidence,
        viewerMemberId,
        centeredMemberId,
      ],
    );

  /*
   * CC-TREE-LIVE-003G3E4
   *
   * Cell B is the already-governed 003G3E3 comparison.
   * Before any additional layout work, re-establish the
   * immutable B-reference safety contract locally.
   */
  const canonicalBranchCounterfactualReferenceCoherentForMatrix =
    useMemo(
      () => {
        const comparison =
          canonicalBranchCounterfactualComparison;

        return (
          comparison !== null &&

          comparison.schema ===
            TREE_RUNTIME_CANONICAL_BRANCH_COUNTERFACTUAL_SCHEMA &&

          comparison.availability ===
            'OBSERVED' &&

          comparison.controlParity ===
            true &&

          comparison.controlWorldParity ===
            true &&

          comparison.controlFailureParity ===
            true &&

          comparison.invalidJoinCount ===
            0 &&

          comparison.sourceMutationAllowed ===
            false &&

          comparison.causalAttributionAllowed ===
            false &&

          comparison.legacyRetirementAuthorized ===
            false &&

          comparison.productionAuthorityChanged ===
            false &&

          comparison.privateIdentityPublished ===
            false &&

          comparison.readOnly ===
            true &&

          comparison.repairExecuted ===
            false
        );
      },
      [
        canonicalBranchCounterfactualComparison,
      ],
    );

  /*
   * The expensive C/D experiment may begin only after:
   *
   * - authenticated runtime;
   * - current matrix-plan authority;
   * - current semantic-plan authority;
   * - coherent 003G3E3 B reference;
   * - exact B control parity;
   * - positive semantic substitutions;
   * - positive layout substitutions;
   * - no semantic-only complete repair.
   */
  const branchLayoutInteractionCounterfactualEligible =
    useMemo(
      () =>
        canonicalBranchCounterfactualReferenceCoherentForMatrix ===
          true &&

        isTreeRuntimeBranchLayoutInteractionCounterfactualMatrixPlanAuthorityCoherent(
          branchLayoutInteractionMatrixPlan,
          canonicalBranchLayoutAuthority,
        ) &&

        isTreeRuntimeCanonicalBranchCounterfactualShadowPlanAuthorityCoherent(
          canonicalBranchCounterfactualShadowPlan,
          canonicalBranchLayoutAuthority,
        ) &&

        isTreeRuntimeBranchLayoutInteractionCounterfactualExecutionEligible({
          authenticatedSession,

          plan:
            branchLayoutInteractionMatrixPlan,

          semanticComparison:
            canonicalBranchCounterfactualComparison,
        }),
      [
        authenticatedSession,
        branchLayoutInteractionMatrixPlan,
        canonicalBranchLayoutAuthority,
        canonicalBranchCounterfactualShadowPlan,
        canonicalBranchCounterfactualComparison,
        canonicalBranchCounterfactualReferenceCoherentForMatrix,
      ],
    );

  /*
   * Cell C — layout-only shadow.
   *
   * Never rendered. Never computed before eligibility.
   */
  const branchLayoutInteractionCellCLayout =
    useMemo(
      () => {
        if (
          !branchLayoutInteractionCounterfactualEligible
        ) {
          return null;
        }

        try {
          return computePyramidLayoutCanonicalLayoutShadow(
            {
              structuralMembers,

              visibleMembers:
                members,

              canonicalGeometryRoles,

              canonicalEdges,

              branchLayoutAuthority:
                canonicalBranchLayoutAuthority,

              centeredMemberId,

              youName,
            },

            branchLayoutInteractionMatrixPlan,
          );
        } catch {
          return null;
        }
      },
      [
        branchLayoutInteractionCounterfactualEligible,
        structuralMembers,
        members,
        canonicalGeometryRoles,
        canonicalEdges,
        canonicalBranchLayoutAuthority,
        centeredMemberId,
        youName,
        branchLayoutInteractionMatrixPlan,
      ],
    );

  /*
   * Cell D — canonical semantic branch + canonical-compatible
   * layout shadow.
   *
   * Never rendered. Never computed before eligibility.
   */
  const branchLayoutInteractionCellDLayout =
    useMemo(
      () => {
        if (
          !branchLayoutInteractionCounterfactualEligible
        ) {
          return null;
        }

        try {
          return computePyramidLayoutCanonicalBranchAndLayoutShadow(
            {
              structuralMembers,

              visibleMembers:
                members,

              canonicalGeometryRoles,

              canonicalEdges,

              branchLayoutAuthority:
                canonicalBranchLayoutAuthority,

              centeredMemberId,

              youName,
            },

            branchLayoutInteractionMatrixPlan,

            canonicalBranchCounterfactualShadowPlan,
          );
        } catch {
          return null;
        }
      },
      [
        branchLayoutInteractionCounterfactualEligible,
        structuralMembers,
        members,
        canonicalGeometryRoles,
        canonicalEdges,
        canonicalBranchLayoutAuthority,
        centeredMemberId,
        youName,
        branchLayoutInteractionMatrixPlan,
        canonicalBranchCounterfactualShadowPlan,
      ],
    );

  /*
   * Four-cell comparison remains private in browser memory.
   * No private failure cohort leaves this comparator.
   */
  const branchLayoutInteractionFourCellComparison =
    useMemo(
      () => {
        if (
          !branchLayoutInteractionCounterfactualEligible ||
          !canonicalBranchCounterfactualComparison ||
          !branchLayoutInteractionCellCLayout ||
          !branchLayoutInteractionCellDLayout
        ) {
          return null;
        }

        try {
          return compareTreeRuntimeBranchLayoutInteractionFourCellMatrix({
            authority:
              canonicalBranchLayoutAuthority,

            matrixPlan:
              branchLayoutInteractionMatrixPlan,

            semanticPlan:
              canonicalBranchCounterfactualShadowPlan,

            cellBComparison:
              canonicalBranchCounterfactualComparison,

            controlLayout:
              layout,

            cellCLayout:
              branchLayoutInteractionCellCLayout,

            cellDLayout:
              branchLayoutInteractionCellDLayout,

            structuralMembers,

            productionGeometryEvidence:
              geometryEvidence,

            viewerMemberId,

            centeredMemberId,

            options: {
              cardWidth:
                CARD_W,

              cardHeight:
                CARD_H,

              rowGap:
                TIER_GAP_Y,

              tolerance:
                0.001,

              bezierSamples:
                64,
            },
          });
        } catch {
          return null;
        }
      },
      [
        branchLayoutInteractionCounterfactualEligible,
        canonicalBranchLayoutAuthority,
        branchLayoutInteractionMatrixPlan,
        canonicalBranchCounterfactualShadowPlan,
        canonicalBranchCounterfactualComparison,
        layout,
        branchLayoutInteractionCellCLayout,
        branchLayoutInteractionCellDLayout,
        structuralMembers,
        geometryEvidence,
        viewerMemberId,
        centeredMemberId,
      ],
    );

  const geometryEvidenceIdentity = useMemo(
    () =>
      JSON.stringify({
        status: geometryEvidence.status,
        cardCount: geometryEvidence.cardCount,
        edgeCount: geometryEvidence.edgeCount,
        spouseLinkCount:
          geometryEvidence.spouseLinkCount,
        companionChipCount:
          geometryEvidence.companionChipCount,
        issues: geometryEvidence.issues.map(
          (issue) => ({
            status: issue.status,
            rule: issue.rule,
            memberIds: issue.memberIds ?? [],
            edgeKeys: issue.edgeKeys ?? [],
          }),
        ),
      }),
    [geometryEvidence],
  );

  /*
   * CC-TREE-LIVE-003G3E2
   *
   * Opaque per-observation correlation only.
   * It contains no member, edge, account, or family identity.
   */
  const geometryObservationId =
    useMemo(
      () =>
        [
          'tree-geometry-observation',
          Date.now(),
          Math.random()
            .toString(36)
            .slice(2, 12),
        ].join('-'),
      [geometryEvidence],
    );

  useEffect(() => {
    publishTreeGeometryRuntimeEvidence(
      geometryEvidence,
      authenticatedSession,
      geometryObservationId,
    );
  }, [
    geometryEvidenceIdentity,
    geometryEvidence,
    authenticatedSession,
    geometryObservationId,
  ]);

  useEffect(() => {
    publishTreeRuntimeLegacyAncestryCausalLocalizationEvidence(
      legacyAncestryCausalLocalization,
      authenticatedSession,
      geometryObservationId,
    );
  }, [
    legacyAncestryCausalLocalization,
    authenticatedSession,
    geometryObservationId,
  ]);


  useEffect(() => {
    if (
      !canonicalBranchCounterfactualComparison
    ) {
      return;
    }

    try {
      publishTreeRuntimeCanonicalBranchCounterfactualShadowEvidence(
        canonicalBranchCounterfactualComparison,
        authenticatedSession,
        geometryObservationId,
      );
    } catch {
      // Counterfactual observability may never alter Tree rendering.
    }
  }, [
    canonicalBranchCounterfactualComparison,
    authenticatedSession,
    geometryObservationId,
  ]);

  /*
   * CC-TREE-LIVE-003G3E4
   *
   * Aggregate-only four-cell evidence.
   * No publication exists for an ineligible or invalid matrix.
   */
  useEffect(() => {
    if (
      !branchLayoutInteractionCounterfactualEligible ||
      !branchLayoutInteractionFourCellComparison ||
      branchLayoutInteractionFourCellComparison
        .availability !==
          'OBSERVED'
    ) {
      return;
    }

    try {
      publishTreeRuntimeBranchLayoutInteractionCounterfactualEvidence(
        branchLayoutInteractionFourCellComparison,
        authenticatedSession,
        geometryObservationId,
      );
    } catch {
      // Counterfactual observability may never alter Tree rendering.
    }
  }, [
    branchLayoutInteractionCounterfactualEligible,
    branchLayoutInteractionFourCellComparison,
    authenticatedSession,
    geometryObservationId,
  ]);

  // ---------- Governed readable / overview framing ----------
  //
  // The final projection already owns canonical card coordinates. This
  // consumer changes viewport pan and zoom only. It never alters cards,
  // edges, spouse links, connector routes, relationship records, or bounds.
  const READABLE_WORLD_ZOOM = 0.8;
  const OVERVIEW_MINIMUM_ZOOM = 0.2;
  const FRAME_MAXIMUM_ZOOM = 1;

  const worldCanvasIdentity = useMemo(() => {
    return [
      bounds.minX,
      bounds.maxX,
      bounds.minY,
      bounds.maxY,
      layout.viewerCenterEvidence.requestedViewerMemberId ??
        'no-requested-viewer',
      layout.viewerCenterEvidence.renderedYouMemberId ??
        'no-rendered-viewer',
      layout.viewerCenterEvidence.renderedYouX ??
        'no-rendered-viewer-x',
      layout.viewerCenterEvidence.renderedYouY ??
        'no-rendered-viewer-y',
      centeredMemberId ?? 'no-centered-member',
      viewMode,
    ].join('|');
  }, [
    bounds.minX,
    bounds.maxX,
    bounds.minY,
    bounds.maxY,
    layout.viewerCenterEvidence,
    centeredMemberId,
    viewMode,
  ]);

  const performGovernedFrame = useCallback(
    (mode: CanonicalTreeWorldCanvasMode) => {
      if (containerSize.w <= 0 || containerSize.h <= 0) return;
      if (isBulkWriting) return;

      if (
        !Number.isFinite(bounds.minX) ||
        !Number.isFinite(bounds.maxX) ||
        !Number.isFinite(bounds.minY) ||
        !Number.isFinite(bounds.maxY)
      ) {
        return;
      }

      const centeredCard =
        centeredMemberId
          ? finalCards.find(
              card => card.memberId === centeredMemberId,
            ) ?? null
          : null;

      const viewerCard =
        finalCards.find(card => card.isYou) ??
        finalCards.find(
          card => card.memberId === viewerMemberId,
        ) ??
        null;

      // pyramidLayout already translates an explicitly centered relative to
      // world origin. Otherwise the rendered viewer card remains the focus.
      const readableFocus =
        centeredCard ??
        viewerCard ?? {
          x: (bounds.minX + bounds.maxX) / 2,
          y: (bounds.minY + bounds.maxY) / 2,
        };

      const padding =
        containerSize.w < 640
          ? 28
          : 80;

      const viewportSnapshot =
        viewportSnapshotRef.current;

      const result =
        resolveCanonicalTreeWorldCanvas({
          mode,
          bounds: {
            minX: bounds.minX,
            maxX: bounds.maxX,
            minY: bounds.minY,
            maxY: bounds.maxY,
          },
          viewportWidth: containerSize.w,
          viewportHeight: containerSize.h,
          viewerPoint: {
            x: readableFocus.x,
            y: readableFocus.y,
          },
          currentFocusPoint: {
            x:
              -viewportSnapshot.pan.x /
              viewportSnapshot.zoom,
            y:
              -viewportSnapshot.pan.y /
              viewportSnapshot.zoom,
          },
          currentZoom:
            viewportSnapshot.zoom,
          readableZoom: READABLE_WORLD_ZOOM,
          overviewMinimumZoom:
            OVERVIEW_MINIMUM_ZOOM,
          maximumZoom: FRAME_MAXIMUM_ZOOM,
          padding,
        });

      setZoom(result.zoom);
      setPan({
        x: -result.focus.x * result.zoom,
        y: -result.focus.y * result.zoom,
      });
      setWorldCanvasEvidence(result);
    },
    [
      bounds.minX,
      bounds.maxX,
      bounds.minY,
      bounds.maxY,
      containerSize.w,
      containerSize.h,
      centeredMemberId,
      finalCards,
      viewerMemberId,
      isBulkWriting,
    ],
  );

  const lastGovernedFrameKey = useRef('');

  useEffect(() => {
    if (!requestedFrameMode) return;
    if (containerSize.w <= 0 || containerSize.h <= 0) return;
    if (isBulkWriting) return;

    const key = [
      requestedFrameMode,
      worldCanvasIdentity,
      containerSize.w,
      containerSize.h,
    ].join('|');

    if (lastGovernedFrameKey.current === key) return;

    lastGovernedFrameKey.current = key;
    performGovernedFrame(requestedFrameMode);
  }, [
    requestedFrameMode,
    worldCanvasIdentity,
    containerSize.w,
    containerSize.h,
    isBulkWriting,
    performGovernedFrame,
  ]);

  const pathFromRoutePoints = (
    points:
      readonly {
        x: number;
        y: number;
      }[],
  ): string =>
    points
      .map(
        (point, index) =>
          `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`,
      )
      .join(' ');

  // Connectors render the per-edge list built during layout, so each line
  // is anchored at its TRUE parent's (x, y) and TRUE child's (x, y). There
  // is NO YOU-axis fabrication here — the from/to coordinates come directly
  // from the placed parent and child cards. (Only the orphan-fallback edges
  // emitted in pass 1 use YOU as `from`, which is correct because those
  // cards have no other ancestor on the canvas to anchor to.)
  const connectors = useMemo(() => {
    const geometryRootByMemberId =
      new Map(
        finalCards.flatMap(
          card =>
            card.memberId
              ? [[
                  card.memberId,
                  card.geometryRoot,
                ] as const]
              : [],
        ),
      );

    return finalEdges.flatMap((e) => {
      if (
        e.routeStatus !== 'ROUTED' ||
        e.routePoints.length < 2
      ) {
        return [];
      }

      const sourceRoots =
        e.fromMemberIds.map(
          memberId =>
            geometryRootByMemberId.get(
              memberId,
            ) ?? 'unresolved',
        );

      if (sourceRoots.length === 0) {
        sourceRoots.push(
          'unresolved',
        );
      }

      const visual =
        resolveCanonicalTreeConnectorVisualPresentation(
          sourceRoots,
        );

      return [{
        id: e.key,
        d:
          pathFromRoutePoints(
            e.routePoints,
          ),
        className:
          visual.className,
        dashed: !e.verified,
      }];
    });
  }, [
    finalEdges,
    finalCards,
  ]);


  // ---------- Pan + Pinch-zoom (multi-pointer events) --------------------
  // Uses POINTER events so a finger / stylus / mouse all share the same
  // gesture handlers. We track every active pointer in `pointersRef`:
  //
  //   • 1 active pointer  → drag-to-pan (single-finger / mouse drag)
  //   • 2 active pointers → pinch-to-zoom (two-finger gesture)
  //                         · distance between fingers controls zoom
  //                         · midpoint between fingers controls pan
  //                         · zoom anchors at the midpoint so the world
  //                           grows / shrinks around the user's fingers
  //                           (no jump to viewport center)
  //
  // touchAction: 'none' on the wrapper tells the browser NOT to handle the
  // gesture itself (which would scroll the page or trigger browser zoom),
  // so the canvas alone receives the pointer events. This is what makes
  // pinch zoom inside the tree box ZOOM THE TREE — never the whole page —
  // exactly as the spec requires.
  const ZOOM_MIN = 0.2;
  const ZOOM_MAX = 3;
  const onPointerDown = (e: React.PointerEvent) => {
    if ((e.target as HTMLElement).closest('button')) return;
    setRequestedFrameMode(null);
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    try {
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    } catch {
      /* setPointerCapture can throw on synthetic events — safe to ignore */
    }
    if (pointersRef.current.size === 1) {
      // Single-pointer drag-to-pan.
      setDragging(true);
      dragRef.current = { x: e.clientX, y: e.clientY, px: pan.x, py: pan.y };
      pinchRef.current = null;
    } else if (pointersRef.current.size === 2) {
      // Second finger touched down — switch to pinch-zoom mode.
      // Capture the starting distance + midpoint so we can compute the
      // zoom factor and pan correction relative to it.
      const pts = Array.from(pointersRef.current.values());
      const dx = pts[0].x - pts[1].x;
      const dy = pts[0].y - pts[1].y;
      const startDist = Math.hypot(dx, dy) || 1;
      const startMid = {
        x: (pts[0].x + pts[1].x) / 2,
        y: (pts[0].y + pts[1].y) / 2,
      };
      pinchRef.current = {
        startDist,
        startZoom: zoom,
        startMid,
        startPan: { x: pan.x, y: pan.y },
      };
      // Cancel any in-progress single-finger pan; the gesture is now pinch.
      dragRef.current = null;
      setDragging(false);
    }
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!pointersRef.current.has(e.pointerId)) return;
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (pointersRef.current.size === 2 && pinchRef.current) {
      // ----- Pinch-zoom -----
      const pts = Array.from(pointersRef.current.values());
      const dx = pts[0].x - pts[1].x;
      const dy = pts[0].y - pts[1].y;
      const dist = Math.hypot(dx, dy) || 1;
      const factor = dist / pinchRef.current.startDist;
      const newZoom = Math.min(
        ZOOM_MAX,
        Math.max(ZOOM_MIN, pinchRef.current.startZoom * factor),
      );
      // Anchor the zoom at the gesture midpoint (in container-local
      // coords). World point under the midpoint at gesture-start must
      // remain under the (possibly moved) midpoint at gesture-now, so
      // the user's fingers feel "stuck" to the same spot in the tree.
      const el = wrapRef.current;
      const rect = el?.getBoundingClientRect();
      if (rect) {
        const startMidLocal = {
          x: pinchRef.current.startMid.x - rect.left,
          y: pinchRef.current.startMid.y - rect.top,
        };
        const nowMidLocal = {
          x: (pts[0].x + pts[1].x) / 2 - rect.left,
          y: (pts[0].y + pts[1].y) / 2 - rect.top,
        };
        // Convert the start midpoint from screen → world coords using the
        // SAME formula as worldTransform (origin = container center + pan,
        // scale = startZoom). Then back-project to screen at the new zoom
        // and add the (current - start) midpoint delta so two-finger drag
        // still pans while pinching.
        const cx = containerSize.w / 2;
        const cy = containerSize.h / 2;
        const worldX =
          (startMidLocal.x - cx - pinchRef.current.startPan.x) /
          pinchRef.current.startZoom;
        const worldY =
          (startMidLocal.y - cy - pinchRef.current.startPan.y) /
          pinchRef.current.startZoom;
        const newPanX = nowMidLocal.x - cx - worldX * newZoom;
        const newPanY = nowMidLocal.y - cy - worldY * newZoom;
        setZoom(newZoom);
        setPan({ x: newPanX, y: newPanY });
      } else {
        setZoom(newZoom);
      }
      return;
    }

    if (dragRef.current && pointersRef.current.size === 1) {
      // ----- Single-finger / mouse pan -----
      setPan({
        x: dragRef.current.px + (e.clientX - dragRef.current.x),
        y: dragRef.current.py + (e.clientY - dragRef.current.y),
      });
    }
  };
  const onPointerUp = (e: React.PointerEvent) => {
    pointersRef.current.delete(e.pointerId);
    try {
      (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    } catch {
      /* see setPointerCapture note above */
    }
    if (pointersRef.current.size === 0) {
      setDragging(false);
      dragRef.current = null;
      pinchRef.current = null;
    } else if (pointersRef.current.size === 1) {
      // A two-finger pinch ended but one finger remains — seamlessly
      // resume drag-to-pan from where the remaining finger sits, so the
      // user can keep panning without lifting and re-pressing.
      pinchRef.current = null;
      const remaining = Array.from(pointersRef.current.values())[0];
      dragRef.current = {
        x: remaining.x,
        y: remaining.y,
        px: pan.x,
        py: pan.y,
      };
      setDragging(true);
    }
  };

  // Wheel zoom — supports BOTH:
  //   • Mouse wheel (any modifier): zooms the tree in/out, anchored at
  //     the cursor position so the point under the cursor stays put.
  //   • Trackpad pinch: browsers translate trackpad pinch into wheel
  //     events with ctrlKey=true, so the same handler covers it.
  // touchAction: 'none' on the canvas wrapper means plain wheel events
  // arrive here without scrolling the page, so we always preventDefault
  // and zoom — the user never has to zoom the BROWSER to inspect the
  // tree (exactly the spec rule "Do not require browser/page zoom to
  // inspect the tree").
  const onWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    setRequestedFrameMode(null);
    const delta = -e.deltaY * 0.0015;
    const el = wrapRef.current;
    const rect = el?.getBoundingClientRect();
    setZoom((z) => {
      const next = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z + delta * z));
      if (rect) {
        // Anchor zoom at the cursor: keep the world point under the
        // cursor stationary across the zoom step.
        const localX = e.clientX - rect.left;
        const localY = e.clientY - rect.top;
        const cx = containerSize.w / 2;
        const cy = containerSize.h / 2;
        const worldX = (localX - cx - pan.x) / z;
        const worldY = (localY - cy - pan.y) / z;
        const newPanX = localX - cx - worldX * next;
        const newPanY = localY - cy - worldY * next;
        setPan({ x: newPanX, y: newPanY });
      }
      return next;
    });
  };




  // Locate always returns to the governed readable viewer frame. It never
  // silently activates full-tree compression.
  const recenter = () => {
    lastGovernedFrameKey.current = '';
    setRequestedFrameMode('readable');

    if (centeredMemberId && onClearCenter) {
      onClearCenter();
    }
  };

  const showOverview = () => {
    lastGovernedFrameKey.current = '';
    setRequestedFrameMode('overview');
  };

  const rowLabels = useMemo(() => {
    const labels: Record<number, string> = {
      3: 'Great-Grandparents',
      2: 'Grandparents',
      1: 'Parents',
      0: 'You',
      [-1]: 'Children',
      // Safety-net overflow band — members whose placement couldn't be
      // proved from the parent-child graph. Self-documenting label so the
      // user immediately understands these cards need a parent linked.
      [-2]: 'Needs connection',
    };
    const tiersInUse = new Set<number>([0]);
    for (const c of finalCards) {
      if (!c.isYou) tiersInUse.add(c.tier);
    }
    return Array.from(tiersInUse)
      .sort((a, b) => b - a)
      .map((tier) => ({
        tier,
        // labels are still positioned relative to the (untransformed) row band
        y: -tier * TIER_GAP_Y,
        label: labels[tier] ?? `Generation ${tier}`,
      }));
  }, [finalCards]);


  const worldTransform = `translate(${containerSize.w / 2 + pan.x}px, ${
    containerSize.h / 2 + pan.y
  }px) scale(${zoom})`;

  /*
   * CC-TREE-LIVE-003G3V
   *
   * Geometry remains world-space. Interactive authoring controls are
   * projected into viewport-local CSS pixels so their target size never
   * shrinks when the Tree enters overview mode.
   */
  const interactionSurfaceByCardId =
    useMemo(() => {
      const result =
        new Map<
          string,
          ReturnType<
            typeof resolveCanonicalTreeInteractionSurfaceProjection
          >
        >();

      if (
        containerSize.w <= 0 ||
        containerSize.h <= 0
      ) {
        return result;
      }

      for (const card of finalCards) {
        const id =
          card.isYou
            ? 'you'
            : card.member?.id ?? null;

        if (!id) continue;

        result.set(
          id,
          resolveCanonicalTreeInteractionSurfaceProjection({
            worldPoint: {
              x: card.x,
              y: card.y,
            },
            pan,
            zoom,
            viewportWidth:
              containerSize.w,
            viewportHeight:
              containerSize.h,
            cardWidth:
              CARD_W,
            cardHeight:
              CARD_H,
            actionTargetSize:
              44,
            panelWidth:
              280,
            panelHeight:
              420,
            panelGap:
              12,
            viewportMargin:
              8,
          }),
        );
      }

      return result;
    }, [
      finalCards,
      pan,
      zoom,
      containerSize.w,
      containerSize.h,
    ]);

  // Find popover anchor card
  const popoverCard = useMemo(() => {
    if (!addPopoverFor) return null;
    if (addPopoverFor === 'you') {
      return finalCards.find((c) => c.isYou) || null;
    }

    return (
      finalCards.find(
        (c) => c.member?.id === addPopoverFor,
      ) || null
    );
  }, [addPopoverFor, finalCards]);

  // Quick-action menu anchor card
  const quickPopoverCard = useMemo(() => {
    if (!quickPopoverFor) return null;
    return (
      finalCards.find(
        (c) => c.member?.id === quickPopoverFor,
      ) || null
    );
  }, [quickPopoverFor, finalCards]);

  const beginAddRelativeChooserFor = (
    id: string,
  ) => {
    /*
     * A Tree chooser is a new authoring transaction.
     * Parent-owned modal/default state is reset BEFORE
     * any relationship choices become available.
     */
    onBeginAddRelativeChooser();
    setQuickPopoverFor(null);
    setAddPopoverFor(id);
  };

  const activateScreenSpaceAddRelative = (
    id: string,
  ) => {
    /*
     * A second activation of the same visible chooser only
     * closes it. Opening a chooser always starts clean.
     */
    if (addPopoverFor === id) {
      setQuickPopoverFor(null);
      setAddPopoverFor(null);
      return;
    }

    beginAddRelativeChooserFor(id);
  };

  const popoverInteractionSurface =
    useMemo(() => {
      if (!popoverCard) {
        return null;
      }

      const id =
        popoverCard.isYou
          ? 'you'
          : popoverCard.member?.id ?? null;

      if (!id) {
        return null;
      }

      return (
        interactionSurfaceByCardId.get(id) ??
        null
      );
    }, [
      popoverCard,
      interactionSurfaceByCardId,
    ]);

  // ---------- Canonical viewer-relative display labels ----------
  // G2 consumes the semantic descriptor already retained by the canonical
  // viewer projection. No downstream relationship recomputation remains.
  const displayLabelById = useMemo(() => {
    const map = new Map<string, string>();

    for (const m of members) {
      const label =
        canonicalViewerProjections
          .get(m.id)
          ?.kinshipSemantics.primary.treeLabel
          ?.trim() ||
        'Needs connection';

      map.set(m.id, label);
    }

    return map;
  }, [
    members,
    canonicalViewerProjections,
  ]);


  // ─── FAMILY UNIT LAYER (companion-card overlay) ─────────────────────────
  //
  // After the strict bloodline pyramid has computed positions for every
  // member it CAN place, walk every spouse_id link in the loaded graph
  // looking for "dangling" partners — members whose ONLY connection to
  // the visible tree is a spouse/partner edge to an already-placed
  // bloodline anchor. For each such member we:
  //
  //   1. Drop their card from `cards` if it landed in the overflow row
  //      (tier -2 / needsConnection). This is what removes Terrance
  //      Lewis Sr. from the bottom "Needs connection" band when he is
  //      married to Chekesha and Chekesha is on the tree.
  //   2. Render a small `FamilyUnitChip` directly beneath their anchor
  //      card with the viewer-perspective in-law label ("Son-in-law to
  //      Marion", "Mother-in-law", "Step-Parent", etc.) so the spouse
  //      stays emotionally visible without needing to be slotted on a
  //      strict generational tier of their own.
  //   3. NEVER touches the database. NEVER mutates members. NEVER
  //      requires the spouse to have parent_ids. Pure presentation.
  //
  // Reciprocity is NOT required — a one-sided spouse_id pointing in
  // either direction is enough to render the chip. The chip is the
  // safety net the spec calls for: "Any spouse/partner/in-law with a
  // valid anchor gets rendered in Family Unit Layer even if pyramid
  // layout fails."
  //
  // When the pyramid layout DID place the partner beside their anchor
  // as a locked couple unit (heart-line + side-by-side), we DO NOT
  // render a chip — the heart connector is the canonical visual and
  // the chip would duplicate it.



  const convergenceStatus =
    canonicalRuntimeConvergence?.status ??
    'BLOCKED';

  const convergenceBlockers =
    canonicalRuntimeConvergence?.blockers ??
    [];

  return (
    <div
      className="relative rounded-3xl bg-gradient-to-br from-slate-50 via-white to-pink-50/40 border border-slate-200 shadow-sm overflow-hidden"
      data-tree-runtime-convergence-status={
        convergenceStatus
      }
      data-tree-runtime-convergence-blockers={
        convergenceBlockers.join(',')
      }
      data-tree-runtime-visible-member-count={
        canonicalRuntimeConvergence
          ?.productionVisibleMemberIds.length ??
        0
      }
      data-tree-runtime-missing-projection-count={
        canonicalRuntimeConvergence
          ?.missingProjectionMemberIds.length ??
        0
      }
      data-tree-runtime-missing-geometry-role-count={
        canonicalRuntimeConvergence
          ?.missingGeometryRoleMemberIds.length ??
        0
      }
      data-tree-runtime-missing-connector-count={
        canonicalRuntimeConvergence
          ?.missingConnectorEdgeIds.length ??
        0
      }
    >
      <span
        className="sr-only"
        data-testid="tree-runtime-convergence-evidence"
      >
        {`Tree runtime convergence: ${convergenceStatus}. Blockers: ${
          convergenceBlockers.length > 0
            ? convergenceBlockers.join(', ')
            : 'none'
        }.`}
      </span>
      <div className="kc-tree-branch-label pointer-events-none absolute top-1 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2 px-3 py-1 text-[10px] font-bold tracking-[0.12em] uppercase">
        <span className="kc-tree-branch-label-maternal">
          Mother's Side
        </span>
        <span className="text-slate-300" aria-hidden>|</span>
        <span className="kc-tree-branch-label-paternal">
          Father's Side
        </span>
      </div>






      {centeredMemberId && onClearCenter && (
        <div className="absolute top-14 right-3 z-20">
          <button
            onClick={recenter}
            className="text-[11px] font-semibold bg-white border border-fuchsia-200 text-fuchsia-700 rounded-full px-3 py-1 shadow hover:bg-fuchsia-50"
            title="Return to my readable tree"
          >
            ← Back to my tree
          </button>
        </div>
      )}




      <div
        className="kc-tree-branch-axis pointer-events-none absolute inset-y-0 left-1/2 -translate-x-1/2 w-px z-0"
        aria-hidden
      />

      {/*
       * touchAction: 'none' below tells the browser NOT to use this
       * element's touch gestures for native scrolling/zooming — so finger
       * drags become pointer-move events we can handle as pan, and pinch
       * is ignored (we expose explicit zoom buttons instead). Without it,
       * a swipe on the tree on iOS/Android scrolls the whole page and the
       * user can't pan to off-screen relatives.
       *
       * Pointer events (vs mouse events) are what make TOUCH panning
       * possible: mousemove never fires from a finger, so before this fix
       * users on tablets/phones literally could not drag clipped cards
       * into view.
       */}
      <div
        ref={wrapRef}
        data-tree-gesture-canvas
        className="relative w-full h-[640px] sm:h-[720px] cursor-grab active:cursor-grabbing select-none"
        style={{ touchAction: 'none' }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onWheel={onWheel}
        onClick={() => {
          setAddPopoverFor(null);
          setQuickPopoverFor(null);
        }}
      >


        <svg
          className="absolute inset-0 pointer-events-none"
          width="100%"
          height="100%"
          style={{ overflow: 'visible' }}
        >
          <g style={{ transform: worldTransform, transformOrigin: '0 0' }}>
            {connectors.map((c) => (
              <path
                key={c.id}
                d={c.d}
                className={c.className}
                strokeWidth={1.5}
                strokeDasharray={c.dashed ? '5 5' : '0'}
                fill="none"
                opacity={0.55}
                strokeLinecap="round"
              />
            ))}
            {finalSpouseLinks.map((l, i) => (
              <line
                key={`sp-${i}`}
                x1={l.a.x}
                y1={l.a.y}
                x2={l.b.x}
                y2={l.b.y}
                className={CANONICAL_TREE_SPOUSE_PARTNER_VISUAL_CLASS}
                strokeWidth={2}
                strokeDasharray="2 4"
                opacity={0.9}
              />
            ))}
          </g>
        </svg>

        <div
          className="absolute top-0 left-0"
          style={{ transform: worldTransform, transformOrigin: '0 0' }}
        >
          {rowLabels.map(({ tier, y, label }) => (
            <React.Fragment key={`row-${tier}`}>
              <div
                className={`absolute -translate-y-1/2 rounded-full ${
                  tier === 0
                    ? 'bg-fuchsia-50/70'
                    : tier > 0
                    ? 'bg-slate-100/50'
                    : 'bg-emerald-50/50'
                }`}
                style={{ left: -1400, top: y, width: 2800, height: CARD_H + 28, zIndex: -1 }}
              />
              <div
                className={`absolute -translate-y-1/2 text-[10px] font-bold uppercase tracking-[0.2em] px-2 py-0.5 rounded-full shadow-sm border ${
                  tier === 0
                    ? 'bg-white text-fuchsia-700 border-fuchsia-200'
                    : tier > 0
                    ? 'bg-white text-slate-600 border-slate-200'
                    : 'bg-white text-emerald-700 border-emerald-200'
                }`}
                style={{ left: -1380, top: y }}
              >
                {label}
              </div>
            </React.Fragment>
          ))}

          {finalSpouseLinks.map((l, i) => {
            const cx = (l.a.x + l.b.x) / 2;
            const cy = (l.a.y + l.b.y) / 2;
            return (
              <div
                key={`heart-${i}`}
                className={`absolute -translate-x-1/2 -translate-y-1/2 w-7 h-7 rounded-full shadow flex items-center justify-center ${CANONICAL_TREE_SPOUSE_HEART_VISUAL_CLASS}`}
                style={{ left: cx, top: cy }}
                title="Spouse / partner"
              >
                <Heart className={`w-3.5 h-3.5 ${CANONICAL_TREE_SPOUSE_HEART_ICON_VISUAL_CLASS}`} />
              </div>
            );
          })}

          {finalCards.map((c) => {
            const id = c.isYou ? 'you' : c.member!.id;
            const centered = !!centeredMemberId && c.member?.id === centeredMemberId;
            // Family Unit chips for this anchor's spouse(s) / partner(s)
            // that the strict pyramid couldn't slot side-by-side. They
            // hang BELOW the card (top: card.y + CARD_H/2 + N) and tap
            // straight through to the partner's profile.
            const chipsForThisCard = !c.isYou && c.member
              ? familyUnitChips.chips.get(c.member.id) ?? []
              : [];
            return (
              <React.Fragment key={id}>
                <ProfileCard
                  card={c}
                  allocatedClusterClass={
                    resolveCanonicalTreeVisibleClusterPaletteClass(
                      visibleClusterPaletteAllocation,
                      c.geometryRoot,
                      c.clusterKey ?? null,
                    )
                  }
                  familyOriginVisual={
                    c.memberId
                      ? familyOriginVisualByMemberId
                          .get(
                            c.memberId,
                          ) ?? null
                      : null
                  }
                  kinshipSignatureSegments={
                    c.memberId
                      ? canonicalParentLineageSignature
                          .segmentTokensByMemberId
                          .get(
                            c.memberId,
                          ) ?? []
                      : []
                  }
                  centered={centered}
                  youName={youName}
                  youPhotoUrl={youPhotoUrl}
                  displayLabel={
                    c.isYou
                      ? undefined
                      : c.needsConnection
                      ? 'Needs connection'
                      : displayLabelById.get(c.member!.id)
                  }
                  onOpen={() => {
                    if (c.isYou) {
                      setAddPopoverFor(null);
                      setQuickPopoverFor(null);

                      if (c.member) {
                        onSelectMember(c.member, 'profile');
                        return;
                      }

                      const selfMember = {
                        id: 'self',
                        ownerUserId: 'self',
                        name: youName,
                        relationship: 'unknown' as const,
                        side: 'self' as const,
                        approvalStatus: 'approved' as const,
                        confirmationsCount: 0,
                        createdAt: new Date().toISOString(),
                        profileState: 'claimed' as const,
                      };

                      onSelectMember(selfMember as any, 'profile');
                    } else if (c.member) {
                      setAddPopoverFor(null);
                      setQuickPopoverFor(null);
                      onSelectMember(c.member, 'profile');
                    }
                  }}
                  onLongPress={() => {
                    beginAddRelativeChooserFor(id);
                  }}
                />
                {chipsForThisCard.length > 0 && c.member && (
                  <div
                    className="absolute pointer-events-none"
                    style={{
                      left: c.x,
                      top: c.y + CARD_H / 2,
                      width: 1,
                      height: 1,
                    }}
                  >
                    <div className="absolute pointer-events-auto -translate-x-1/2" style={{ left: 0, top: 0 }}>
                      {chipsForThisCard.map((chip, i) => (
                        <FamilyUnitChip
                          key={`fu-${c.member!.id}-${chip.member.id}`}
                          member={chip.member}
                          anchorName={c.member!.name.split(' ')[0] || c.member!.name}
                          visualRootClass={
                            resolveCanonicalTreeVisualRootClass(
                              c.geometryRoot,
                            )
                          }
                          derivedLabel={chip.derivedLabel}
                          stackIndex={i}
                          onOpen={() => {
                            setAddPopoverFor(null);
                            setQuickPopoverFor(null);
                            onSelectMember(chip.member, 'profile');
                          }}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </React.Fragment>
            );
          })}




          {quickPopoverCard && quickPopoverCard.member && (
            <QuickActionPopover
              member={quickPopoverCard.member}
              x={quickPopoverCard.x}
              y={quickPopoverCard.y + CARD_H / 2 + 18}
              onViewProfile={() => onSelectMember(quickPopoverCard.member!, 'profile')}
              onAddMemory={() => onSelectMember(quickPopoverCard.member!, 'memory')}
              onAddRelative={() => {
                // Switch from quick menu → a NEW governed chooser session.
                beginAddRelativeChooserFor(
                  quickPopoverCard.member!.id,
                );
              }}
              onClose={() => setQuickPopoverFor(null)}
            />
          )}
        </div>



        {members.length === 0 && (
          <div className="absolute inset-0 flex items-end justify-center pb-20 pointer-events-none">
            <div className="text-center text-slate-500 text-sm bg-white/80 backdrop-blur rounded-full px-4 py-2 border border-slate-200">
              Tap "You" or the + button to add your first relative.
            </div>
          </div>
        )}
      </div>
      {/* CC-TREE-LIVE-003G3V GESTURE CANVAS END */}
        <div
          data-tree-interaction-surface
          className="absolute left-0 top-0 z-30 pointer-events-none"
          style={{
            width: containerSize.w,
            height: containerSize.h,
            touchAction: 'auto',
          }}
        >
          {finalCards.map((card) => {
            const id =
              card.isYou
                ? 'you'
                : card.member?.id ?? null;

            if (!id) {
              return null;
            }

            const projection =
              interactionSurfaceByCardId.get(
                id,
              );

            if (
              !projection ||
              !projection.targetVisible
            ) {
              return null;
            }

            const familyOriginVisual =
              card.memberId
                ? familyOriginVisualByMemberId
                    .get(
                      card.memberId,
                    ) ?? null
                : null;

            const hasCertifiedFamilyOriginVisual =
              familyOriginVisual?.failClosed ===
                false &&
              Boolean(
                familyOriginVisual.baseClass,
              );

            const allocatedClusterClass =
              resolveCanonicalTreeVisibleClusterPaletteClass(
                visibleClusterPaletteAllocation,
                card.geometryRoot,
                card.clusterKey ??
                  null,
              );

            const interactionVisualRootClass =
              [
                resolveCanonicalTreeVisualRootClass(
                  card.geometryRoot,
                ),

                hasCertifiedFamilyOriginVisual
                  ? 'kc-tree-family-origin'
                  : null,

                hasCertifiedFamilyOriginVisual
                  ? familyOriginVisual
                      ?.baseClass
                  : allocatedClusterClass,

                hasCertifiedFamilyOriginVisual
                  ? familyOriginVisual
                      ?.variantClass
                  : null,
              ]
                .filter(Boolean)
                .join(' ');

            const label =
              card.isYou
                ? 'Add a relative'
                : `Add a relative connected to ${
                    card.member?.name ??
                    'this person'
                  }`;

            return (
              <button
                key={`screen-add-${id}`}
                type="button"
                data-tree-screen-space-add-control
                className="pointer-events-auto absolute min-w-[44px] min-h-[44px] rounded-full bg-transparent flex items-center justify-center p-0"
                style={{
                  left:
                    projection
                      .addControlCenter.x,
                  top:
                    projection
                      .addControlCenter.y,
                  width:
                    projection
                      .actionTargetSize,
                  height:
                    projection
                      .actionTargetSize,
                  transform:
                    'translate(-50%, -50%)',
                  touchAction:
                    'manipulation',
                }}
                aria-label={label}
                title={label}
                onPointerDown={(event) => {
                  event.stopPropagation();
                }}
                onPointerUp={(event) => {
                  event.stopPropagation();

                  if (
                    event.pointerType ===
                      'mouse' &&
                    event.button !== 0
                  ) {
                    return;
                  }

                  activateScreenSpaceAddRelative(
                    id,
                  );
                }}
                onClick={(event) => {
                  event.stopPropagation();

                  /*
                   * Pointer activation already happened on pointerup.
                   * Browser-generated pointer clicks normally carry
                   * detail > 0 and must NOT toggle the state a second
                   * time. Keyboard / assistive button activation uses
                   * detail === 0 and remains fully supported.
                   */
                  if (event.detail === 0) {
                    activateScreenSpaceAddRelative(
                      id,
                    );
                  }
                }}
              >
                <span
                  data-tree-screen-space-add-visual
                  aria-hidden="true"
                  className={`${interactionVisualRootClass} ${CANONICAL_TREE_ADD_BUTTON_VISUAL_CLASS} pointer-events-none w-7 h-7 rounded-full text-white shadow-lg flex items-center justify-center border-2 border-white`}
                >
                  <Plus className="w-4 h-4" />
                </span>
              </button>
            );
          })}

          {popoverCard &&
            popoverInteractionSurface &&
            popoverInteractionSurface
              .targetVisible && (
              <div
                data-tree-screen-space-add-relative
                className="pointer-events-auto"
              >
                <AddRelativePopover
                  member={
                    popoverCard.member
                  }
                  isYou={
                    !!popoverCard.isYou
                  }
                  viewerMemberId={
                    viewerMemberId
                  }
                  x={
                    popoverInteractionSurface
                      .panelTopLeft.x +
                    popoverInteractionSurface
                      .panelWidth /
                      2
                  }
                  y={
                    popoverInteractionSurface
                      .panelTopLeft.y
                  }
                  width={
                    popoverInteractionSurface
                      .panelWidth
                  }
                  maxHeight={
                    popoverInteractionSurface
                      .panelHeight
                  }
                  onPick={(
                    rel,
                    side,
                    parentId,
                    customLabel,
                    relationshipWorkflow,
                  ) =>
                    onAddRelative(
                      rel,
                      side,
                      undefined,
                      parentId,
                      customLabel,
                      relationshipWorkflow,
                    )
                  }
                  onClose={() =>
                    setAddPopoverFor(
                      null,
                    )
                  }
                />
              </div>
            )}
        </div>



      {/*
       * CC-TREE-LIVE-003G3V
       * Reserved bottom-control rail.
       *
       * Legend and viewport controls participate in normal
       * document flow rather than independently overlaying
       * the Tree canvas. Narrow screens stack the two groups;
       * wider screens may present them side-by-side.
       */}
      <div
        data-tree-bottom-control-rail
        className="relative z-20 flex flex-col gap-2 border-t border-slate-100 bg-white/80 px-3 py-2 sm:flex-row sm:items-center sm:justify-between"
      >
  <div
          data-tree-legend
          className="flex flex-wrap items-center justify-center gap-3 rounded-full border border-slate-200 bg-white/90 px-3 py-1.5 text-[10px] text-slate-600 shadow backdrop-blur"
        >
          <span className="flex items-center gap-1">
            <ShieldCheck className="w-3 h-3 text-emerald-600" /> Verified
          </span>
          <span className="flex items-center gap-1">
            <Clock className="w-3 h-3 text-amber-600" /> Pending
          </span>
          <span className="flex items-center gap-1">
            <Heart className="w-3 h-3 text-rose-500" /> Spouse link
          </span>
        </div>

  <div
          data-tree-zoom-controls
          className="flex w-full items-center justify-center gap-1 rounded-2xl border border-slate-300 bg-white/95 px-1.5 py-1.5 shadow-lg backdrop-blur-md sm:w-auto"
        >
          <button
            onClick={() => {
              setRequestedFrameMode(null);
              setZoom((z) => Math.min(3, z + 0.2));
            }}
            className="w-9 h-9 rounded-xl hover:bg-slate-100 active:bg-slate-200 text-slate-700 flex items-center justify-center transition"
            title="Zoom in"
            aria-label="Zoom in"
          >
            <ZoomIn className="w-5 h-5" />
          </button>
          <button
            onClick={() => {
              setRequestedFrameMode(null);
              setZoom((z) => Math.max(0.2, z - 0.2));
            }}
            className="w-9 h-9 rounded-xl hover:bg-slate-100 active:bg-slate-200 text-slate-700 flex items-center justify-center transition"
            title="Zoom out"
            aria-label="Zoom out"
          >
            <ZoomOut className="w-5 h-5" />
          </button>
          <button
            onClick={showOverview}
            className="w-9 h-9 rounded-xl hover:bg-slate-100 active:bg-slate-200 text-slate-700 flex items-center justify-center transition"
            title={
              worldCanvasEvidence?.mode === 'overview' &&
              worldCanvasEvidence.overviewFitDisposition ===
                'MINIMUM-SCALE-PREVENTS-COMPLETE-FIT'
                ? 'Overview active — pan horizontally to reach remaining branches'
                : 'Show full-tree overview'
            }
            aria-label="Show full-tree overview"
          >
            <Eye className="w-5 h-5" />
          </button>
          <button
            onClick={recenter}
            className="w-9 h-9 rounded-xl bg-fuchsia-50 hover:bg-fuchsia-100 active:bg-fuchsia-200 text-fuchsia-700 flex items-center justify-center transition"
            title={
              centeredMemberId
                ? 'Return to my readable tree'
                : 'Center readable view on me'
            }
            aria-label="Center readable view on me"
          >
            <Locate className="w-5 h-5" />
          </button>
          <span className="text-[11px] font-bold text-slate-600 px-2 min-w-[42px] text-center tabular-nums">
            {Math.round(zoom * 100)}%
          </span>
        </div>
      </div>

      <div className="px-4 py-2 text-center text-[11px] text-slate-500 bg-white/60 border-t border-slate-100">
        Tap a card to open · long-press or tap + to add a relative · pinch with two fingers to zoom · drag to pan · scroll to zoom
      </div>


    </div>
  );
};

export default FamilyTreeView;
